"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  addUpiPaymentMethod,
  closeUpiQrForPaymentAttempt,
  createUpiQrForPaymentAttempt,
  getOrCreateRazorpayCustomerForUser,
  getUpiQrAvailability,
  removePaymentMethod,
} from "@/actions/payment";
import { createClientLogger } from "@/lib/logging/client";
import {
  beginNativePaymentNavigation,
  endNativePaymentNavigation,
  isNativePaymentShell,
} from "@/lib/native/payment-navigation";
import { razorpayPaymentEmail } from "@/lib/payments/razorpay-contact";
import { PHONE_VERIFICATION_REQUIRED } from "@/lib/payments/verified-payer-contract";
import { createRandomId } from "@/lib/random-id";
import { openRazorpayCheckout } from "@/lib/razorpay-checkout";
import {
  createRazorpayCustomPayment,
  type RazorpayCustomPaymentSuccess,
  verifyRazorpayVpa,
} from "@/lib/razorpay-custom-checkout";
import {
  buildRazorpayUpiIntentCheckoutOptions,
  checkVpaFormat,
  clearPaymentMethodDraft,
  describeVpaHolderName,
  getCardDraftValidationError,
  isPaymentOutcomeUnknown,
  isUpiNoAppFailure,
  isVpaRejection,
  normalizeCardExpiry,
  normalizeCardNumber,
  normalizeVpa,
  PAYMENT_CONFIRMATION_PENDING_MESSAGE,
  PAYMENT_REFUNDED_MESSAGE,
  type PaymentMethodRecord,
  parseCardExpiry,
  readPaymentMethodDraft,
  UPI_ID_NOT_FOUND_MESSAGE,
  UPI_ID_REJECTED_MESSAGE,
  UPI_NO_APP_MESSAGE,
  UPI_QR_AWAITING_MESSAGE,
  writePaymentMethodDraft,
} from "../lib";
import {
  type CardErrorField,
  cardFeedbackProperties,
  getCardPaymentFeedback,
} from "../lib/card-feedback";
import {
  clearLegacyUnavailableUpiApps,
  loadNativeUpiAppAvailability,
  loadUpiAppAvailability,
  UPI_ANY_APP,
  type UpiAppAvailability,
} from "../lib/upi-apps";
import {
  describeQrOption,
  detectUpiSurface,
  recommendsProviderIntent,
  supportsDirectUpiAppSelection,
  type UpiSurface,
} from "../lib/upi-surface";

export type RazorpayCustomPaymentMode =
  | "upi_intent"
  | "upi_qr"
  | "upi"
  | "card";

/**
 * A live UPI QR the payer is looking at.
 *
 * Held on the controller rather than inside the drawer because `launchPayment`
 * must not resolve while it is on screen: the QR *is* the payment attempt, and
 * the caller's "Pay" button stays in its launching state until the payer
 * scans, cancels, or the code expires.
 */
/**
 * The live UPI QR, when the account can mint one.
 *
 * Apps are deliberately not in here any more. Choosing an app is a *method
 * choice*, so it belongs in the payment-method sheet beside the cards and the
 * saved UPI IDs — routing it through a second drawer made the payer open one
 * surface to pick UPI and another to pick which UPI. A QR still needs its own
 * screen, because a code has to be looked at.
 */
export type UpiPaymentSession = {
  qrCodeId: string;
  /** Razorpay's `image_content` — the QR payload. */
  upiUri: string;
  expiresAtIso: string;
  amountMinor: number;
  currency: string;
  merchantName: string;
};

/**
 * How the QR drawer can end. `settled` means the poll saw the booking paid —
 * the caller has already navigated. `dismissed` means we do not know, which is
 * not the same as failed.
 */
type UpiDrawerOutcome = { kind: "settled" } | { kind: "dismissed" };

/**
 * A UPI app handoff in flight, and the screen the payer waits on.
 *
 * The screen exists because the handoff cannot report itself. Razorpay's SDK
 * fires the deep link as a top-level navigation and then decides "no app" from
 * the payer's next touch on a document that never went hidden — see
 * `supportsDirectUpiAppSelection` for the read of its source. That verdict
 * arrives identically whether the app is missing, the payer declined the
 * browser's "Open in PhonePe?" prompt, or Safari refused a scheme fired outside
 * a user gesture. Treating it as terminal is what made a working Google Pay
 * look broken.
 *
 * So it is treated as advisory. The drawer stays, the app keeps its tile, and
 * **Open App** re-fires the link from inside a real tap — which is the one
 * thing Safari reliably honours, and precisely what the first automatic attempt
 * (fired after `preparePaymentAttempt` and the customer lookup have both been
 * awaited) no longer has.
 */
export type UpiIntentHandoff = {
  shortcode: string;
  appName: string;
  iconUrl: string | null;
  amountMinor: number;
  currency: string;
  merchantName: string;
  /** 1 is the automatic attempt; every later one came from Open App. */
  attempts: number;
  /** An Open App tap is in flight — the button shows a spinner, not a retry. */
  retrying: boolean;
  /**
   * The app has not come forward. Set by the provider's no-app verdict, or by
   * our own watch seeing no app switch at all. Drives the advisory line; it is
   * never a reason to remove the app or close the drawer.
   */
  appOpenFailed: boolean;
};

/**
 * How an app handoff ends.
 *
 * `paid` is the SDK's own success. `settled` is the poll seeing the webhook land
 * while the payer was away — the same signal the QR drawer settles on. `failed`
 * is the provider genuinely refusing (a decline, a bad order), which is the only
 * outcome that may close this screen against the payer's wishes.
 */
type UpiIntentOutcome =
  | { kind: "paid"; payment: RazorpayCustomPaymentSuccess }
  | { kind: "settled" }
  | { kind: "dismissed" }
  | { kind: "failed"; message: string };
/**
 * `verify_phone` is a VIEW of this sheet, not a drawer on top of it.
 *
 * The mobile-number field already lived inside `add_card` and `add_upi`, so the
 * verification step belongs in the same state machine — which is also what keeps
 * the selected method, the basket and the pinned idempotency key alive across
 * it. Stacking a second vaul drawer would fight this one for the focus trap and
 * the scroll lock, and would sit under the global auth drawer's z-10000 anyway.
 */
export type RazorpayCustomPaymentSheetView =
  | "methods"
  | "add_card"
  | "add_upi"
  | "verify_phone";

type ActionResult<T> =
  | { ok: true; data: T; message?: string }
  | {
      ok: false;
      message: string;
      code?: string;
      /**
       * The refusal is a request for input, not a payment failure: the sheet is
       * already open on the view that collects what is missing. Callers must
       * clear their processing state and stay quiet — a "Payment Failed"
       * overlay (z-1100) would otherwise land on top of the drawer (z-100) and
       * read as a dead end.
       */
      inputRequired?: boolean;
    };

export type RazorpayOrderPaymentAttempt = {
  paymentAttemptId: number;
  /**
   * The thing being paid for: an event/venue booking id, a Hiive Money top-up
   * id — whatever the flow's confirm action keys on. Deliberately not called
   * `bookingId`: this controller is shared by non-booking checkouts too.
   */
  referenceId: number;
  provider: "razorpay";
  amountMinor: number;
  currency: string;
  orderId: string;
  razorpayKeyId: string;
};

export type RazorpayOrderPaymentConfirmationInput = {
  referenceId: number;
  paymentAttemptId: number;
  razorpayOrderId: string;
  razorpayPaymentId: string;
  razorpaySignature?: string;
  cardNickname?: string;
  savePaymentMethod?: boolean;
};

/**
 * How long a UPI handoff may produce nothing before we record the silence.
 *
 * The clock starts at launch, not at the tap, because the tap happens inside
 * the provider's own modal where we cannot see it. Twenty seconds is long
 * enough that a payer still reading that modal is rarely counted, and short
 * enough that the Safari-alert case — which resolves in about two seconds and
 * then never resolves again — is recorded while the session is still live.
 */
const HANDOFF_UNRESOLVED_AFTER_MS = 20_000;

/** What a handoff watch saw before it was stopped. */
type UpiHandoffObservation = {
  appSwitchObserved: boolean;
  msToAppSwitch: number | null;
};

export type RazorpayCustomPaymentEventName =
  | "payment_method_validation_failed"
  | "payment_method_selected"
  | "payment_method_add_started"
  | "payment_method_added"
  | "payment_method_removed"
  | "payment_validation_failed"
  | "payment_attempt_failed"
  | "payment_provider_failed"
  | "payment_outcome_pending"
  | "payment_attempt_created"
  | "payment_handoff_unresolved"
  | "payment_launch_coalesced";

type RazorpayCustomPaymentEventProperties = Record<string, unknown>;

export type RazorpayPaymentNotesInput<
  TPrepared extends RazorpayOrderPaymentAttempt,
> = {
  prepared: TPrepared;
  mode: RazorpayCustomPaymentMode;
  source: "saved" | "draft";
  selectedMethod: PaymentMethodRecord | null;
  cardNickname: string;
  saveCard: boolean;
  saveUpi: boolean;
};

type UseRazorpayCustomPaymentInput<
  TPrepared extends RazorpayOrderPaymentAttempt,
  TConfirmed,
> = {
  /** Identifies what is being paid for; forwarded to `preparePaymentAttempt`. */
  referenceId: number;
  razorpayKeyId: string | null;
  paymentMethods: PaymentMethodRecord[];
  payer: {
    name: string | null;
    email: string | null;
    contact: string | null;
  };
  merchantName: string;
  description: string;
  preparePaymentAttempt: (input: {
    referenceId: number;
    idempotencyKey: string;
  }) => Promise<ActionResult<TPrepared>>;
  confirmPayment: (
    input: RazorpayOrderPaymentConfirmationInput,
  ) => Promise<ActionResult<TConfirmed>>;
  buildReturnUrl?: (prepared: TPrepared) => string | undefined;
  buildNotes: (
    input: RazorpayPaymentNotesInput<TPrepared>,
  ) => Record<string, string>;
  blockedMessage?: string | null;
  /**
   * Whether this deployment requires a verified phone before payment. Server
   * flag, threaded down from the page — the client only uses it to decide
   * whether to skip a doomed round trip; the server enforces it regardless.
   */
  requiresVerifiedPhone?: boolean;
  /**
   * Re-reads the payer snapshot after a successful verification. Supplied by the
   * page (it owns the server action), so this hook stays free of a direct action
   * import and remains usable from every checkout surface.
   */
  refreshPayer?: () => Promise<{
    name: string | null;
    email: string | null;
    contact: string | null;
  } | null>;
  disabled?: boolean;
  themeColor?: string;
  reuseIdempotencyKey?: boolean;
  /**
   * Ask the server whether this payment has settled yet. Resolve `true` once it
   * reached a terminal state.
   *
   * Only the QR flow needs it, and it is the reason the QR flow can exist at
   * all. A QR payment reaches Razorpay with `order_id: null` and is stitched to
   * the booking by its notes inside `qr_code.credited` — so the browser has
   * nothing to observe and no `razorpay_payment_id` to confirm with. The
   * caller's own status check (which re-reads the booking) is the only honest
   * answer, and every payment page already has one for the pending-collect path.
   *
   * Omit it and the QR row is simply not offered.
   */
  checkPaymentSettled?: () => Promise<boolean>;
  /**
   * Cards only — a card hold (authorize now, capture later) can't be placed
   * with UPI, which debits on approval. Hides every UPI row and saved UPI ID.
   */
  cardOnly?: boolean;
  /**
   * A card hold: the payment is meant to stop at "authorized" (captured later
   * by the host's approval), so an authorized confirmation is the success
   * state, not an unknown outcome.
   */
  authorizeOnly?: boolean;
  onPaymentEvent?: (
    eventName: RazorpayCustomPaymentEventName,
    properties?: RazorpayCustomPaymentEventProperties,
  ) => void;
};

export type RazorpayCustomPaymentController<TConfirmed = unknown> = {
  /** Only cards are offered (a card hold). */
  cardOnly: boolean;
  paymentMode: RazorpayCustomPaymentMode;
  hasChosenPaymentMethod: boolean;
  selectedStoredMethod: PaymentMethodRecord | null;
  selectedPaymentLabel: string;
  selectedPaymentSubtitle: string | null;
  selectedPaymentBadge: string;
  /** The selected method's logo, when it has one. Drives the footer mark. */
  selectedPaymentIconUrl: string | null;
  storedCards: PaymentMethodRecord[];
  storedUpiMethods: PaymentMethodRecord[];
  sheetOpen: boolean;
  sheetView: RazorpayCustomPaymentSheetView;
  sheetError: string | null;
  cardErrorField: CardErrorField | null;
  cardName: string;
  cardNickname: string;
  cardNumber: string;
  cardExpiry: string;
  cardCvv: string;
  saveCard: boolean;
  saveUpi: boolean;
  /**
   * Verified mobile number from the server, or null. There is no editable field
   * for this any more: a payment form is not an identity boundary, so a number
   * becomes usable only by surviving an OTP.
   */
  verifiedContactPhone: string | null;
  /** True while the sheet is showing the verification step. */
  isVerifyingPhone: boolean;
  /** True while a typed UPI ID is being validated and saved. */
  isValidatingUpi: boolean;
  /** Id of the method currently being removed, so its row can show progress. */
  removingMethodId: number | null;
  /**
   * Remove a saved method. For a card this deletes Razorpay's token first — the
   * only change a settled credential accepts, since the token API has no update.
   */
  removeMethod: (methodId: number) => Promise<void>;
  /**
   * Name the accepted UPI handle is registered to, when the provider told us.
   * Shown back as confirmation — the same reassurance a UPI app gives before you
   * approve a request.
   */
  upiHolderName: string | null;
  /** The provider confirmed the typed address resolves to a UPI account. */
  upiVerified: boolean;
  isLaunching: boolean;
  setSheetOpen: (open: boolean) => void;
  setSheetView: (view: RazorpayCustomPaymentSheetView) => void;
  setCardName: (value: string) => void;
  setCardNickname: (value: string) => void;
  setCardNumberInput: (value: string) => void;
  setCardExpiryInput: (value: string) => void;
  setCardCvvInput: (value: string) => void;
  setUpiId: (value: string) => void;
  upiId: string;
  setSaveCard: (value: boolean) => void;
  setSaveUpi: (value: boolean) => void;
  openMethodSheet: () => void;
  closeMethodSheet: () => void;
  selectStoredMethod: (method: PaymentMethodRecord) => void;
  selectUpiIntent: () => void;
  /** Choose "Scan QR code" — the surface that cannot dead-end on a missing app. */
  selectUpiQr: () => void;
  /** Choose a UPI app from the sheet. */
  selectUpiApp: (shortcode: string) => void;
  /** The app rows the sheet should show, or null while unknown. */
  upiApps: UpiAppAvailability | null;
  /** False only while the mount-time app probe can still change the choices. */
  upiAppProbeComplete: boolean;
  /** Which app is currently selected, so its row can show as chosen. */
  selectedUpiApp: string | null;
  /** What this device can honour, so each row can promise one true thing. */
  upiSurface: UpiSurface;
  /** Inside the Capacitor shell, where `webview_intent` owns the handoff. */
  nativePaymentShell: boolean;
  /**
   * Whether the provider-managed "Pay by UPI app" row may wear the
   * "Recommended" badge. False on iOS mobile web, the one surface where that
   * route can end at an alert this page cannot see or recover from.
   */
  providerIntentRecommended: boolean;
  /**
   * Whether the QR row is offered at all. False when the caller gave no way to
   * observe settlement — a QR nobody is watching would hang on "waiting" until
   * it expired, which is a worse dead end than the one this replaces.
   */
  supportsUpiQr: boolean;
  /**
   * The UPI app handoff on screen, or null. Drives `UpiPaymentDrawer`'s app
   * view — the screen with Open App on it.
   */
  upiIntentHandoff: UpiIntentHandoff | null;
  /** Re-fire the deep link from inside this tap. The recovery that works. */
  retryUpiIntentApp: () => void;
  /** Give up on the app and go back to the method list. */
  cancelUpiIntentHandoff: () => void;
  /** The open UPI drawer, or null. Drives `UpiPaymentDrawer`. */
  upiSession: UpiPaymentSession | null;
  /** Dismiss the UPI drawer and settle the pending `launchPayment`. */
  cancelUpiSession: () => void;
  startAddCard: () => void;
  startAddUpi: () => void;
  /**
   * Accept the typed card / UPI draft as the selected method. Named `apply`, not
   * `use`: a `use*` member on a controller object reads as a React hook to both
   * people and the rules-of-hooks lint, and it is neither.
   */
  applyCardDraft: () => void;
  applyUpiDraft: () => Promise<void>;
  /** Show the verification step without losing the selected method. */
  startPhoneVerification: () => void;
  /**
   * Called by the verification view once the OTP succeeded. Refreshes the payer
   * snapshot and returns to review — it deliberately does NOT re-launch the
   * payment. The person must press Pay again: silently charging a card because
   * an OTP for a *phone number* succeeded is not consent.
   */
  completePhoneVerification: () => Promise<void>;
  launchPayment: () => Promise<ActionResult<TConfirmed>>;
  resetLaunch: () => void;
  /** Discard the pinned reuse key so the next launch starts a fresh attempt. */
  resetIdempotencyKey: () => void;
};

const logger = createClientLogger(
  "features/payments/components/use-razorpay-custom-payment",
);

const normalizeDigits = (value: string) => value.replace(/\D/g, "");

/**
 * Flatten the provider's refusal into event properties.
 *
 * `reason` is what the queen funnel groups on, so it gets the machine cause and
 * falls back through the code before it settles for a generic label — a bare
 * "Payment failed" row is exactly what made the last round of drop-offs
 * undiagnosable.
 */
const providerErrorProperties = (failure: {
  message: string;
  providerError?: import("@/lib/razorpay-custom-checkout").RazorpayProviderError;
}) => {
  const error = failure.providerError;
  return {
    reason:
      error?.reason ??
      error?.code ??
      (isPaymentOutcomeUnknown(failure.message)
        ? "payment_status_unknown"
        : "provider_error_unspecified"),
    payment_outcome: isPaymentOutcomeUnknown(failure.message)
      ? "unknown"
      : "failed",
    provider_error_code: error?.code ?? null,
    provider_error_source: error?.source ?? null,
    provider_error_step: error?.step ?? null,
    // Missing IDs mean the client cannot identify the payment. Only a server
    // lookup can establish whether the provider created a payment object.
    provider_payment_id: error?.paymentId ?? null,
  };
};

const createIdempotencyKey = () => createRandomId();

/**
 * Pull the server's payer snapshot off a prepared attempt.
 *
 * Read structurally rather than by widening `TPrepared`: every payment-preparing
 * action returns it, but the generic is shared with older callers, and a missing
 * snapshot must degrade to "keep what we had" rather than fail to compile.
 */
const readPreparedPayer = (
  prepared: unknown,
): { contactPhone: string | null; contactEmail: string | null } | null => {
  if (typeof prepared !== "object" || prepared === null) return null;
  const payer = (prepared as { payer?: unknown }).payer;
  if (typeof payer !== "object" || payer === null) return null;
  const { contactPhone, contactEmail } = payer as {
    contactPhone?: unknown;
    contactEmail?: unknown;
  };
  return {
    contactPhone: typeof contactPhone === "string" ? contactPhone : null,
    contactEmail: typeof contactEmail === "string" ? contactEmail : null,
  };
};

const getReusablePaymentToken = (method: PaymentMethodRecord) =>
  method.metadata?.cardTokenId ?? null;

const getLatestSelectablePaymentMethod = (
  methods: PaymentMethodRecord[],
): PaymentMethodRecord | null =>
  methods
    .filter(
      (method) =>
        method.category === "card" ||
        (method.category === "upi" && Boolean(method.metadata?.upiVpa?.trim())),
    )
    .sort(
      (left, right) =>
        new Date(right.lastUsedAtIso).getTime() -
        new Date(left.lastUsedAtIso).getTime(),
    )[0] ?? null;

const getInitialPaymentMode = (
  method: PaymentMethodRecord | null,
): RazorpayCustomPaymentMode => {
  if (method?.category === "card") return "card";
  if (method?.category === "upi") return "upi";
  return "upi_intent";
};

const getInitialUpiId = (method: PaymentMethodRecord | null) =>
  method?.category === "upi" ? (method.metadata?.upiVpa ?? "") : "";

export const getPaymentMethodDisplayLabel = (method: PaymentMethodRecord) => {
  if (method.category !== "card") return method.label;
  const nickname = method.metadata?.nickname?.trim();
  if (nickname) return nickname;
  const issuer = method.metadata?.cardIssuer?.trim();
  const network = method.metadata?.cardNetwork?.trim();
  if (issuer && network) return `${issuer} ${network} Card`;
  if (network) return `${network} Card`;
  return method.label;
};

export const getPaymentMethodDisplaySubtitle = (
  method: PaymentMethodRecord,
) => {
  if (method.category === "card") {
    const last4 = method.metadata?.cardLast4?.trim();
    return last4 ? `•••••• ${last4} || Secured` : method.subtitle;
  }
  return method.subtitle;
};

export const getPaymentMethodBadge = (
  method: PaymentMethodRecord | null,
  mode: RazorpayCustomPaymentMode,
) => {
  if (method?.category === "card") {
    return (
      method.metadata?.cardNetwork?.trim() ||
      method.metadata?.cardIssuer?.trim() ||
      "Card"
    );
  }
  if (method?.category === "upi" || mode === "upi" || mode === "upi_intent") {
    return "UPI";
  }
  return "Card";
};

const getPaymentModeCategory = (mode: RazorpayCustomPaymentMode) =>
  mode === "upi_intent" ? "upi" : mode;

export function useRazorpayCustomPayment<
  TPrepared extends RazorpayOrderPaymentAttempt,
  TConfirmed,
>({
  referenceId,
  razorpayKeyId,
  paymentMethods,
  payer,
  merchantName,
  description,
  preparePaymentAttempt,
  confirmPayment,
  buildReturnUrl,
  buildNotes,
  blockedMessage,
  requiresVerifiedPhone = false,
  refreshPayer,
  disabled = false,
  themeColor = "#0d1017",
  reuseIdempotencyKey = false,
  checkPaymentSettled,
  cardOnly = false,
  authorizeOnly = false,
  onPaymentEvent,
}: UseRazorpayCustomPaymentInput<
  TPrepared,
  TConfirmed
>): RazorpayCustomPaymentController<TConfirmed> {
  const idempotencyKeyRef = useRef<string | null>(null);
  /*
   * The list = the server's prop, plus what was added here, minus what was
   * removed here.
   *
   * NOT a `useState` seeded from the prop with an effect to re-sync. That was the
   * first attempt and it broke in a way worth recording: the page refetches its
   * server data during checkout, and each new prop identity overwrote the local
   * list — so adding a second UPI ID left the first one showing alone the moment
   * the server caught up to it. Observed in the browser, not theorised.
   *
   * Deriving instead means prop churn cannot clobber anything, and the local
   * layers dissolve on their own: once the server's list carries an addition,
   * dedupe by id collapses the two copies.
   */
  const [locallyAddedMethods, setLocallyAddedMethods] = useState<
    PaymentMethodRecord[]
  >([]);
  const [locallyRemovedMethodIds, setLocallyRemovedMethodIds] = useState<
    number[]
  >([]);

  const methods = useMemo(() => {
    const removed = new Set(locallyRemovedMethodIds);
    const seen = new Set<number>();
    const merged: PaymentMethodRecord[] = [];
    // Local additions lead: they are the newest, and this sheet reads
    // most-recent-first.
    for (const method of [...locallyAddedMethods, ...paymentMethods]) {
      if (removed.has(method.id) || seen.has(method.id)) continue;
      if (cardOnly && method.category !== "card") continue;
      seen.add(method.id);
      merged.push(method);
    }
    return merged;
  }, [cardOnly, locallyAddedMethods, locallyRemovedMethodIds, paymentMethods]);

  const initialPaymentMethod = useMemo(
    () => getLatestSelectablePaymentMethod(methods),
    [methods],
  );
  const [paymentMode, setPaymentMode] = useState<RazorpayCustomPaymentMode>(
    () => (cardOnly ? "card" : getInitialPaymentMode(initialPaymentMethod)),
  );
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetView, setSheetView] =
    useState<RazorpayCustomPaymentSheetView>("methods");
  const [upiId, setUpiId] = useState(() =>
    getInitialUpiId(initialPaymentMethod),
  );
  // Server-resolved and server-owned. Seeded from the prop and refreshed after a
  // successful verification, so the sheet reflects the credential rather than
  // whatever is typed into it.
  const [verifiedContactPhone, setVerifiedContactPhone] = useState<
    string | null
  >(payer.contact ?? null);
  const [verifiedContactEmail, setVerifiedContactEmail] = useState<
    string | null
  >(payer.email ?? null);
  const [cardName, setCardName] = useState(payer.name ?? "");
  const [cardNickname, setCardNickname] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [cardExpiry, setCardExpiry] = useState("");
  const [cardCvv, setCardCvv] = useState("");
  const [saveCard, setSaveCard] = useState(false);
  /*
   * Always true now, and kept only because it still rides along in the payment
   * notes. Adding a UPI ID stores it — that is the whole point of a list you can
   * keep several in and remove from — so there is no longer a question to ask.
   */
  const [saveUpi, setSaveUpi] = useState(true);
  const [selectedStoredMethod, setSelectedStoredMethod] =
    useState<PaymentMethodRecord | null>(initialPaymentMethod);
  const [hasChosenPaymentMethod, setHasChosenPaymentMethod] = useState(
    () => initialPaymentMethod !== null,
  );
  const [cardErrorField, setCardErrorField] = useState<CardErrorField | null>(
    null,
  );
  const paymentAuditRef = useRef<{
    payment_attempt_id: number;
    provider_order_id: string;
    started_at: number;
  } | null>(null);
  const [sheetError, setSheetError] = useState<string | null>(null);
  const [isLaunching, setIsLaunching] = useState(false);
  const [isValidatingUpi, setIsValidatingUpi] = useState(false);
  const [removingMethodId, setRemovingMethodId] = useState<number | null>(null);
  const [upiHolderName, setUpiHolderName] = useState<string | null>(null);
  /**
   * The provider confirmed this address resolves to a UPI account. Separate
   * from `upiHolderName` because the name comes back masked, so a confirmation
   * has to be able to stand on its own.
   */
  const [upiVerified, setUpiVerified] = useState(false);

  const storedCards = useMemo(
    () => methods.filter((method) => method.category === "card"),
    [methods],
  );
  const storedUpiMethods = useMemo(
    () =>
      methods.filter(
        (method) =>
          method.category === "upi" && Boolean(method.metadata?.upiVpa?.trim()),
      ),
    [methods],
  );
  const activeSelectedStoredMethod = useMemo(
    () =>
      selectedStoredMethod?.category === paymentMode
        ? selectedStoredMethod
        : null,
    [paymentMode, selectedStoredMethod],
  );

  const validateCardDraft = useCallback(() => {
    return getCardDraftValidationError({
      name: cardName,
      number: cardNumber,
      expiry: cardExpiry,
      cvv: cardCvv,
    });
  }, [cardCvv, cardExpiry, cardName, cardNumber]);

  /**
   * Close any payment window left open by a previous attempt.
   *
   * The redirect that fixes native checkout also destroys the document that
   * opened the lease, so `launchPayment`'s own `finally` never runs on the path
   * that matters. Landing back on a checkout screen — whether via the return
   * route or by navigating here fresh — is the reliable moment to release it.
   * The native TTL is the backstop, not the mechanism.
   */
  useEffect(() => {
    void endNativePaymentNavigation();
  }, []);

  useEffect(() => {
    if (!initialPaymentMethod) return;
    setSelectedStoredMethod((current) => current ?? initialPaymentMethod);
    setPaymentMode((current) =>
      cardOnly
        ? "card"
        : current === "upi_intent"
          ? getInitialPaymentMode(initialPaymentMethod)
          : current,
    );
    setHasChosenPaymentMethod(true);
    if (initialPaymentMethod.category === "upi") {
      setUpiId(
        (current) => current || initialPaymentMethod.metadata?.upiVpa || "",
      );
    }
  }, [cardOnly, initialPaymentMethod]);

  /*
   * Restore a draft the person already typed.
   *
   * A checkout gets interrupted in ways an ordinary form does not — the UPI
   * handoff can come back to a reloaded document, a native web view can be
   * evicted — and every one of those remounts this hook with its fields empty.
   * Restoring in an effect rather than a `useState` initialiser on purpose: a
   * lazy initialiser that reads storage renders different markup on the server
   * and the client, which is a hydration mismatch on every checkout.
   *
   * The card number, expiry and CVV are not part of the draft and never will be
   * — see `payment-method-draft.ts`.
   */
  const draftRestoredRef = useRef(false);
  useEffect(() => {
    if (draftRestoredRef.current) return;
    draftRestoredRef.current = true;
    const draft = readPaymentMethodDraft();
    if (!draft) return;
    // A saved method is server truth and also drives `paymentMode`, so it wins.
    if (!initialPaymentMethod && draft.upiId) {
      setUpiId((current) => current || draft.upiId);
      setSaveUpi((current) => current || draft.saveUpi);
    }
    if (draft.cardName) setCardName((current) => current || draft.cardName);
    if (draft.cardNickname) {
      setCardNickname((current) => current || draft.cardNickname);
    }
  }, [initialPaymentMethod]);

  // Declared after the restore, so on the first commit the ref is already set
  // and this cannot overwrite a stored draft with the empty initial state.
  useEffect(() => {
    if (!draftRestoredRef.current) return;
    writePaymentMethodDraft({ upiId, saveUpi, cardName, cardNickname });
  }, [cardName, cardNickname, saveUpi, upiId]);

  /**
   * What this browser can actually do with a UPI deep link.
   *
   * Read once: it is a property of the device, and re-deriving it per render
   * would make the sheet's copy depend on render timing.
   */
  const [upiSurface] = useState<UpiSurface>(() => detectUpiSurface());
  const [nativePaymentShell] = useState(() => isNativePaymentShell());
  /**
   * Whether this surface can list app tiles at all. Mobile web always can (from
   * Razorpay's static list); the native shell only once its OS has said what is
   * installed — see `directUpiAppSelectionEnabled` below.
   */
  const upiAppListSupported = supportsDirectUpiAppSelection(upiSurface);

  /**
   * Whether the provider will actually mint a code for us.
   *
   * Starts false and only ever turns on, so the row cannot appear and then be
   * taken away mid-checkout. QR Codes is an on-demand Razorpay feature and an
   * account without it refuses every call, so offering the row on faith would
   * put the recommended option one tap from an error.
   */
  const [upiQrAvailable, setUpiQrAvailable] = useState(false);
  const canObserveSettlement = typeof checkPaymentSettled === "function";
  // A card-only checkout passes no `checkPaymentSettled`, so no QR row.
  const supportsUpiQr = canObserveSettlement && upiQrAvailable;

  /**
   * Which UPI apps the sheet can offer as one-tap rows.
   *
   * Loaded up front rather than at Pay time, because the tiles live in the
   * payment-method sheet now — the payer picks their app in the same list as
   * their cards, and a list that had to wait for a button press would not be a
   * list. Null until answered, so the section renders nothing rather than
   * flashing an empty card.
   *
   * Mobile web lists Razorpay's static platform list. Inside the Capacitor
   * shell the list is the intersection of Razorpay's (built with
   * `webview_intent`) and what the OS confirmed is installed — which only a
   * binary with `HiivePaymentNavigation.listUpiApps` can answer. An older
   * binary answers UNIMPLEMENTED, the list stays null, and the sheet keeps the
   * single provider-managed row it always had.
   */
  const [upiApps, setUpiApps] = useState<UpiAppAvailability | null>(null);
  const [upiAppProbeComplete, setUpiAppProbeComplete] = useState(false);
  /** The app the payer chose, and the one `Pay` will hand off to. */
  const [selectedUpiApp, setSelectedUpiApp] = useState<string | null>(null);

  /**
   * Whether a named-app tile may drive `createPayment(data, { app })` here.
   *
   * Mobile web: always (the pre-existing rule). Native shell: only once the
   * binary has said what is installed. That is the whole gate between "tiles
   * like the web" and "today's behaviour": no answer, no tiles, and every tap
   * goes through the provider-managed `webview_intent` checkout as before.
   */
  const nativeUpiAppsDetected = upiApps?.source === "native_installed";
  const directUpiAppSelectionEnabled =
    upiAppListSupported && (!nativePaymentShell || nativeUpiAppsDetected);
  /**
   * The label every direct-app event carries, so native tiles can be told
   * apart from mobile-web ones in the funnel — they fail for different
   * reasons (Safari's prompt versus an app the OS said was there).
   */
  const directUpiHandoffStrategy = nativePaymentShell
    ? "native_direct_app"
    : "direct_app";
  /**
   * iOS mobile web keeps the provider-managed row but loses the badge. The
   * route can still end at Safari's own error alert, so it must not be the one
   * the sheet pushes ahead of a card or a typed UPI ID, which cannot.
   *
   * Nor in a native shell whose OS just said no UPI app is installed: the row
   * stays as a fallback, but recommending it would push the payer at a list of
   * apps we know this phone does not have.
   */
  const nativeFoundNoUpiApp =
    nativeUpiAppsDetected &&
    upiApps.apps.length === 0 &&
    !upiApps.supportsAnyApp;
  const providerIntentRecommended =
    recommendsProviderIntent(upiSurface, nativePaymentShell) &&
    !nativeFoundNoUpiApp;

  useEffect(() => {
    // Runs on every surface, not only where tiles load: the release that wrote
    // this key hid apps on iOS too, and an iPhone never asks for tiles again.
    clearLegacyUnavailableUpiApps();

    /**
     * Logged once the strategy is actually known — in the native shell that is
     * after the binary answers, because an old build and a new one end up on
     * different paths from the same JS.
     */
    const logConfigured = (
      strategy: string,
      extra: Record<string, unknown> = {},
    ) => {
      logger.info("[payments] upi_handoff_surface_configured", {
        platform: upiSurface.platform,
        nativePaymentShell,
        directAppSelectionEnabled: strategy.endsWith("direct_app"),
        strategy,
        ...extra,
      });
    };

    if (!upiAppListSupported || !razorpayKeyId) {
      logConfigured(
        upiSurface.isDesktop ? "desktop_qr_or_collect" : "provider_checkout",
      );
      setUpiApps(null);
      setUpiAppProbeComplete(true);
      return;
    }
    let active = true;
    const probe = nativePaymentShell
      ? loadNativeUpiAppAvailability(razorpayKeyId)
      : loadUpiAppAvailability(razorpayKeyId);
    void probe
      .then((availability) => {
        if (!active) return;
        if (!availability) {
          // An older binary, or one whose detection could not answer: keep the
          // provider-managed row, exactly as before this change.
          logConfigured("provider_checkout", {
            nativeUpiAppDetection: "unavailable",
          });
          setUpiApps(null);
          return;
        }
        setUpiApps(availability);
        logConfigured(
          nativePaymentShell ? "native_direct_app" : "android_direct_app",
          nativePaymentShell ? { nativeUpiAppDetection: "detected" } : {},
        );
        logger.info("[payments] upi_app_list_loaded", {
          platform: upiSurface.platform,
          appCount: availability.apps.length,
          supportsAnyApp: availability.supportsAnyApp,
          source: availability.source,
        });
      })
      .catch((error) => {
        // No list means no tiles. Guessing one is what produces "Unable to open
        // UPI app", so an unanswered probe offers nothing instead.
        logger.warn("[payments] upi_app_list_load_failed", {
          platform: upiSurface.platform,
          detail: error instanceof Error ? error.message : String(error),
        });
      })
      .finally(() => {
        if (active) setUpiAppProbeComplete(true);
      });
    return () => {
      active = false;
    };
  }, [
    nativePaymentShell,
    razorpayKeyId,
    upiAppListSupported,
    upiSurface.isDesktop,
    upiSurface.platform,
  ]);

  useEffect(() => {
    if (!canObserveSettlement) return;
    let active = true;
    void getUpiQrAvailability()
      .then((result) => {
        if (active && result.ok) setUpiQrAvailable(result.data.available);
      })
      .catch(() => {
        // Fails closed: an unanswered probe leaves the row hidden.
      });
    return () => {
      active = false;
    };
  }, [canObserveSettlement]);

  const selectedPaymentLabel = activeSelectedStoredMethod
    ? getPaymentMethodDisplayLabel(activeSelectedStoredMethod)
    : paymentMode === "card"
      ? cardNickname.trim() || cardName.trim() || "New Card"
      : paymentMode === "upi_intent"
        ? // Name the app they picked; the CTA should say what Pay will open.
          (upiApps?.apps.find((app) => app.shortcode === selectedUpiApp)
            ?.name ??
          (selectedUpiApp === UPI_ANY_APP ? "Any UPI app" : "UPI app"))
        : paymentMode === "upi_qr"
          ? "UPI QR code"
          : upiId.trim() || "UPI ID";
  const selectedPaymentSubtitle = activeSelectedStoredMethod
    ? getPaymentMethodDisplaySubtitle(activeSelectedStoredMethod)
    : paymentMode === "card"
      ? normalizeDigits(cardNumber).length >= 4
        ? `•••• ${normalizeDigits(cardNumber).slice(-4)} | Secured`
        : "Credit or debit card"
      : paymentMode === "upi_intent"
        ? // No third line. The mark and the app's name already say what Pay
          // will do, and the footer only has room to truncate a sentence into
          // "Opens the app to appr…", which reads as breakage rather than help.
          null
        : paymentMode === "upi_qr"
          ? describeQrOption(upiSurface)
          : (upiHolderName ?? "UPI ID");
  /**
   * The selected method's own mark, for the footer. Only UPI apps have one; a
   * card or a typed UPI ID falls back to the text badge.
   */
  const selectedPaymentIconUrl =
    paymentMode === "upi_intent" && selectedUpiApp
      ? (upiApps?.apps.find((app) => app.shortcode === selectedUpiApp)
          ?.iconUrl ?? null)
      : null;

  const selectedPaymentBadge = getPaymentMethodBadge(
    activeSelectedStoredMethod,
    paymentMode,
  );

  const emitPaymentEvent = useCallback(
    (
      eventName: RazorpayCustomPaymentEventName,
      properties: RazorpayCustomPaymentEventProperties = {},
    ) => {
      const audit = paymentAuditRef.current;
      const outcomeUnknown =
        eventName === "payment_attempt_failed" &&
        (properties.payment_outcome === "unknown" ||
          (typeof properties.reason === "string" &&
            isPaymentOutcomeUnknown(properties.reason)));
      onPaymentEvent?.(outcomeUnknown ? "payment_outcome_pending" : eventName, {
        telemetry_version: 2,
        ...(audit
          ? {
              payment_attempt_id: audit.payment_attempt_id,
              provider_order_id: audit.provider_order_id,
              elapsed_ms: Date.now() - audit.started_at,
            }
          : {}),
        selected_payment_mode: paymentMode,
        selected_saved_payment_method: Boolean(activeSelectedStoredMethod),
        selected_payment_method_category:
          activeSelectedStoredMethod?.category ??
          getPaymentModeCategory(paymentMode),
        payment_method_category:
          activeSelectedStoredMethod?.category ??
          getPaymentModeCategory(paymentMode),
        payment_method_source: activeSelectedStoredMethod ? "saved" : "draft",
        upi_client_platform: upiSurface.platform,
        upi_direct_app_selection_enabled: directUpiAppSelectionEnabled,
        ...(paymentMode === "upi_intent"
          ? {
              upi_handoff_strategy:
                directUpiAppSelectionEnabled && selectedUpiApp
                  ? directUpiHandoffStrategy
                  : "provider_checkout",
            }
          : {}),
        ...properties,
      });
    },
    [
      activeSelectedStoredMethod,
      directUpiAppSelectionEnabled,
      directUpiHandoffStrategy,
      onPaymentEvent,
      paymentMode,
      selectedUpiApp,
      upiSurface.platform,
    ],
  );

  /**
   * Watch a UPI app handoff for the outcome that produces no outcome.
   *
   * This is the blind spot the production trace kept running into. When Safari
   * cannot open a provider-generated `phonepe://`, it shows its own native
   * "the address is invalid" alert — which JavaScript cannot see, cannot catch
   * and cannot dismiss. The checkout stays open behind it, Razorpay never emits
   * `payment.failed`, so the launch promise never settles and not one event is
   * recorded. In the funnel that payer is indistinguishable from someone still
   * making up their mind, which is exactly why the dead end went unmeasured.
   *
   * `app_switch_observed` is the discriminator, and it is honest about what it
   * knows: a real handoff backgrounds the document, so if the tab was never
   * hidden then no app ever came to the foreground. Coupled with
   * `upi_handoff_strategy`, an Android direct handoff with no app switch is a
   * failed launch, while a provider-checkout one may simply be a payer reading
   * the modal — so the event reports the observation and leaves the conclusion
   * to whoever reads it.
   */
  const watchUpiHandoff = useCallback(
    (properties: RazorpayCustomPaymentEventProperties) => {
      const noSwitchObserved: UpiHandoffObservation = {
        appSwitchObserved: false,
        msToAppSwitch: null,
      };
      if (typeof document === "undefined") return () => noSwitchObserved;

      const startedAt = Date.now();
      let appSwitchObserved = document.visibilityState === "hidden";
      let msToAppSwitch: number | null = appSwitchObserved ? 0 : null;

      const onVisibilityChange = () => {
        if (document.visibilityState !== "hidden" || appSwitchObserved) return;
        appSwitchObserved = true;
        msToAppSwitch = Date.now() - startedAt;
      };
      document.addEventListener("visibilitychange", onVisibilityChange);

      const timer = setTimeout(() => {
        emitPaymentEvent("payment_handoff_unresolved", {
          ...properties,
          unresolved_after_ms: HANDOFF_UNRESOLVED_AFTER_MS,
          app_switch_observed: appSwitchObserved,
          ...(msToAppSwitch === null
            ? {}
            : { ms_to_app_switch: msToAppSwitch }),
        });
        logger.warn("[payments] upi_handoff_unresolved", {
          platform: upiSurface.platform,
          strategy: String(properties.upi_handoff_strategy ?? "unknown"),
          shortcode: String(properties.selected_upi_app ?? "none"),
          appSwitchObserved,
          unresolvedAfterMs: HANDOFF_UNRESOLVED_AFTER_MS,
        });
      }, HANDOFF_UNRESOLVED_AFTER_MS);

      /**
       * Returns what the launch learned, so the settled path can say on its own
       * event whether an app was ever reached. A failure with no app switch is
       * a handoff that never happened; one with a switch is a payer who got
       * there and came back.
       */
      return () => {
        clearTimeout(timer);
        document.removeEventListener("visibilitychange", onVisibilityChange);
        return { appSwitchObserved, msToAppSwitch };
      };
    },
    [emitPaymentEvent, upiSurface.platform],
  );

  /**
   * The last address the provider check has run for, and how it came out, so
   * leaving and re-entering the field does not re-ask about an unchanged id.
   */
  const probedVpaRef = useRef<{ vpa: string; ok: boolean } | null>(null);

  /**
   * Check the typed UPI ID as soon as the field is left.
   *
   * Two checks with very different standing. The format check is ours, costs
   * nothing, and *does* refuse — it only ever rejects strings that cannot be a
   * VPA. The provider check is run in shadow: its verdict is logged and nothing
   * else, because `verifyVpa` rejects both for "no such address" and for "this
   * account cannot run that check", and our server-side validator already
   * proves the second one happens here. Blocking on it before the logs show it
   * discriminates would refuse every UPI payment from everyone.
   *
   * Promote it to a blocking check once `razorpay_verify_vpa_rejected` shows
   * real `not_found` verdicts rather than a uniform account gate.
   */
  /**
   * Check a typed UPI ID. Driven by "Use this UPI ID", never by the field.
   *
   * Deliberately not on blur. Blur fires when the keyboard dismisses and when
   * somebody taps away mid-thought, so it spends a ~1.6s round trip judging an
   * address that is still being typed, and reports a verdict nobody asked for.
   * The button is the moment a person says they are done, and it already has a
   * "Checking…" state that explains the wait.
   *
   * Two checks, both of which refuse. The format check is ours and rejects only
   * what cannot be a VPA. The provider check is `verifyVpa`, which — with the
   * server-side validator returning the entitlement 400 for every address on
   * this account — is the only thing standing between a wrong UPI ID and a
   * Razorpay order that gets refused with no payment object behind it.
   *
   * It refuses on `not_found` alone. `unavailable` means we could not check and
   * must never block: if the entitlement is ever withdrawn, UPI keeps working
   * and the charge goes back to being the judge. Results are memoised per
   * address, so re-pressing the button costs nothing.
   */
  const checkUpiDraft = useCallback(async (): Promise<boolean> => {
    const format = checkVpaFormat(upiId);
    if (!format.ok) {
      // Silent on an untouched field: the placeholder already says the shape.
      if (upiId.trim()) setSheetError(format.message);
      return false;
    }

    if (!razorpayKeyId) return true;
    if (probedVpaRef.current?.vpa === format.vpa) {
      return probedVpaRef.current.ok;
    }

    setSheetError(null);
    setIsValidatingUpi(true);
    try {
      const verdict = await verifyRazorpayVpa(razorpayKeyId, format.vpa);
      probedVpaRef.current = {
        vpa: format.vpa,
        ok: verdict.status !== "not_found",
      };
      logger.info("[payments] upi_precheck", {
        status: verdict.status,
        named: Boolean(verdict.customerName),
        detail: verdict.detail ?? "none",
      });

      if (verdict.status === "not_found") {
        setSheetError(UPI_ID_NOT_FOUND_MESSAGE);
        setUpiHolderName(null);
        setUpiVerified(false);
        emitPaymentEvent("payment_method_validation_failed", {
          selected_payment_mode: "upi",
          payment_method_category: "upi",
          payment_method_source: "draft",
          reason: "vpa_not_found",
        });
        return false;
      }

      // Restores the verified line. It used to come from the server validator,
      // which answers nothing on this account, so it never showed at all.
      setUpiHolderName(describeVpaHolderName(verdict.customerName));
      setUpiVerified(verdict.status === "exists");
      return true;
    } finally {
      setIsValidatingUpi(false);
    }
  }, [emitPaymentEvent, razorpayKeyId, upiId]);

  const validateUpiDraft = useCallback(() => {
    // Was a non-empty check and nothing else, so a phone number or a bare
    // username reached a real Razorpay order and died at the collect submit —
    // with no payment object created, which is why those orders look untouched
    // in the dashboard. This is the one gate no account setting can disable.
    const format = checkVpaFormat(upiId);
    return format.ok ? null : format.message;
  }, [upiId]);

  const openMethodSheet = useCallback(() => {
    setSheetView("methods");
    setSheetError(null);
    setSheetOpen(true);
  }, []);

  /**
   * Ask for the missing payment details instead of reporting a failure.
   *
   * An incomplete card or Collect draft is not an error anybody can act on from
   * a toast. Open the sheet on the view that can finish it and return
   * `inputRequired`, which keeps the caller's failure overlay off the top of
   * this drawer. A completely unchosen method is handled earlier by opening the
   * picker itself.
   */
  const requestPaymentDetails = useCallback(
    (mode: RazorpayCustomPaymentMode, error: string | null) => {
      setSheetError(error);
      // With something saved the picker is the honest first screen — the mode
      // may only be a default. With nothing saved it would offer the two "Add"
      // rows and nothing else, so skip straight to the form.
      const hasSavedMethod =
        storedCards.length > 0 || storedUpiMethods.length > 0;
      setSheetView(
        hasSavedMethod ? "methods" : mode === "card" ? "add_card" : "add_upi",
      );
      setSheetOpen(true);
    },
    [storedCards.length, storedUpiMethods.length],
  );

  const closeMethodSheet = useCallback(() => {
    setSheetOpen(false);
    setSheetView("methods");
    setSheetError(null);
  }, []);

  const selectStoredMethod = useCallback(
    (method: PaymentMethodRecord) => {
      setSelectedUpiApp(null);
      if (method.category !== "card" && method.category !== "upi") return;
      setSheetError(null);
      setPaymentMode(method.category);
      setSelectedStoredMethod(method);
      setHasChosenPaymentMethod(true);
      if (method.category === "upi") {
        setUpiId(method.metadata?.upiVpa ?? "");
      }
      setSheetOpen(false);
      emitPaymentEvent("payment_method_selected", {
        selected_payment_mode: method.category,
        selected_saved_payment_method: true,
        selected_payment_method_category: method.category,
        payment_method_category: method.category,
        payment_method_source: "saved",
        payment_method_id: method.id,
      });
    },
    [emitPaymentEvent],
  );

  const selectUpiIntent = useCallback(() => {
    setSheetError(null);
    setPaymentMode("upi_intent");
    setSelectedUpiApp(null);
    setSelectedStoredMethod(null);
    setHasChosenPaymentMethod(true);
    setSheetOpen(false);
    emitPaymentEvent("payment_method_selected", {
      selected_payment_mode: "upi_intent",
      selected_saved_payment_method: false,
      selected_payment_method_category: "upi",
      payment_method_category: "upi",
      payment_method_source: "intent",
      upi_flow: "intent",
      upi_handoff_strategy: "provider_checkout",
    });
  }, [emitPaymentEvent]);

  const [upiSession, setUpiSession] = useState<UpiPaymentSession | null>(null);

  /**
   * The half-finished `launchPayment` promise the QR drawer owns.
   *
   * `launchPayment` must not resolve while a live code is on screen — the caller
   * would re-enable Pay under it and invite a second payment for the same
   * booking. So the branch parks its resolver here and the drawer settles it on
   * scan, cancel or expiry. A ref rather than state because settling must not
   * wait on a re-render.
   */
  const upiResolverRef = useRef<((outcome: UpiDrawerOutcome) => void) | null>(
    null,
  );

  const settleUpiSession = useCallback((outcome: UpiDrawerOutcome) => {
    const resolve = upiResolverRef.current;
    upiResolverRef.current = null;
    setUpiSession(null);
    resolve?.(outcome);
  }, []);

  /**
   * The app handoff on screen, and the machinery that keeps it honest.
   *
   * Same shape as the QR drawer: `launchPayment` must not resolve while this is
   * up, or the caller re-enables Pay under a payment that may be completing in
   * another app. The resolver is parked here and the drawer settles it.
   */
  const [upiIntentHandoff, setUpiIntentHandoff] =
    useState<UpiIntentHandoff | null>(null);
  const upiIntentResolverRef = useRef<
    ((outcome: UpiIntentOutcome) => void) | null
  >(null);
  /**
   * Fires one more deep link for the handoff on screen.
   *
   * Held in a ref, not rebuilt per render, because Open App must reach the SDK
   * inside the tap that called it — an extra await before the navigation is how
   * the automatic attempt loses its user activation in the first place.
   */
  const upiIntentAttemptRef = useRef<(() => void) | null>(null);
  /**
   * Attempts fired for the handoff on screen. A ref as well as state because
   * the attempt closure and the dismissal event both read it outside a render.
   */
  const upiIntentHandoffAttemptsRef = useRef(0);
  /**
   * Did any app ever come to the foreground for this handoff?
   *
   * `document.visibilityState` is the only honest answer available: a real
   * handoff backgrounds the page. It decides the advisory line, and it decides
   * what a dismissal means — nothing was started if no app was ever reached, so
   * the payer belongs back at the method list rather than under a "waiting for
   * your payment" banner.
   */
  const upiIntentSwitchRef = useRef<{ observed: boolean; at: number | null }>({
    observed: false,
    at: null,
  });

  const settleUpiIntentHandoff = useCallback((outcome: UpiIntentOutcome) => {
    const resolve = upiIntentResolverRef.current;
    upiIntentResolverRef.current = null;
    upiIntentAttemptRef.current = null;
    setUpiIntentHandoff(null);
    resolve?.(outcome);
  }, []);

  /**
   * Open App. The whole reason this screen exists.
   *
   * Synchronous down to the SDK call so the navigation stays inside the tap.
   */
  const retryUpiIntentApp = useCallback(() => {
    const attempt = upiIntentAttemptRef.current;
    if (!attempt) return;
    setUpiIntentHandoff((current) =>
      current
        ? { ...current, retrying: true, attempts: current.attempts + 1 }
        : current,
    );
    attempt();
    /**
     * A short cooldown, not a wait on the attempt.
     *
     * On iOS the previous attempt does not resolve until the payer's next
     * touch, so holding the button disabled until then would eat that touch and
     * make the second tap the one that works. This blocks a double-fire and
     * hands the control straight back.
     */
    window.setTimeout(() => {
      setUpiIntentHandoff((current) =>
        current ? { ...current, retrying: false } : current,
      );
    }, 1200);
  }, []);

  const cancelUpiIntentHandoff = useCallback(() => {
    settleUpiIntentHandoff({ kind: "dismissed" });
  }, [settleUpiIntentHandoff]);

  /**
   * Watch the handoff: was an app ever reached, and did the payment land?
   *
   * One effect for both because they answer the same question from two sides.
   * The poll is the caller's own status check, so a payment that completed while
   * the payer was in their app settles this screen even though the SDK's
   * `payment.success` never fired — the common case when a deep link succeeds
   * but the return trip does not restore the JS callback.
   */
  /**
   * Read as primitives, so the effect below re-arms on a new *attempt* and on
   * nothing else. Keying it on the whole handoff object would restart the
   * unresolved timer every time the screen re-rendered — including the render
   * the timer itself causes, which is an event every 20 seconds forever.
   */
  const handoffShortcode = upiIntentHandoff?.shortcode ?? null;
  const handoffAttempts = upiIntentHandoff?.attempts ?? 0;

  useEffect(() => {
    if (!handoffShortcode) return;

    const onVisibilityChange = () => {
      if (document.visibilityState !== "hidden") return;
      if (upiIntentSwitchRef.current.observed) return;
      upiIntentSwitchRef.current = { observed: true, at: Date.now() };
      logger.info("[payments] upi_app_switch_observed", {
        platform: upiSurface.platform,
        shortcode: handoffShortcode,
        attempts: handoffAttempts,
      });
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    /**
     * No app switch this long after a handoff means no app came forward, so say
     * so on the screen rather than leaving a spinner to imply progress. This is
     * also the event that made the dead end countable at all: before it, a payer
     * stranded behind Safari's alert left no trace of any kind.
     */
    const unresolvedTimer = window.setTimeout(() => {
      if (upiIntentSwitchRef.current.observed) return;
      setUpiIntentHandoff((current) =>
        current ? { ...current, appOpenFailed: true } : current,
      );
      emitPaymentEvent("payment_handoff_unresolved", {
        payment_method_category: "upi",
        payment_method_source: "intent",
        upi_handoff_strategy: directUpiHandoffStrategy,
        selected_upi_app: handoffShortcode,
        unresolved_after_ms: HANDOFF_UNRESOLVED_AFTER_MS,
        app_switch_observed: false,
        handoff_attempts: handoffAttempts,
      });
    }, HANDOFF_UNRESOLVED_AFTER_MS);

    const poll = checkPaymentSettled
      ? window.setInterval(() => {
          void (async () => {
            let settled = false;
            try {
              settled = await checkPaymentSettled();
            } catch {
              // A failed check is not a failed payment. The next tick may well
              // answer, and the payer may still be inside their app.
              return;
            }
            if (settled) settleUpiIntentHandoff({ kind: "settled" });
          })();
        }, 3000)
      : null;

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.clearTimeout(unresolvedTimer);
      if (poll !== null) window.clearInterval(poll);
    };
  }, [
    checkPaymentSettled,
    directUpiHandoffStrategy,
    emitPaymentEvent,
    handoffAttempts,
    handoffShortcode,
    settleUpiIntentHandoff,
    upiSurface.platform,
  ]);

  const selectUpiQr = useCallback(() => {
    setSheetError(null);
    setPaymentMode("upi_qr");
    setSelectedUpiApp(null);
    setSelectedStoredMethod(null);
    setHasChosenPaymentMethod(true);
    setSheetOpen(false);
    emitPaymentEvent("payment_method_selected", {
      selected_payment_mode: "upi_qr",
      selected_saved_payment_method: false,
      selected_payment_method_category: "upi",
      payment_method_category: "upi",
      payment_method_source: "qr",
      upi_flow: "qr",
    });
  }, [emitPaymentEvent]);

  /**
   * Pick a UPI app from the sheet.
   *
   * This is an ordinary method selection — the same shape as choosing a saved
   * card — so it closes the sheet and lets Pay do the work. It deliberately
   * does not charge on tap: the sheet is where you choose, the button is where
   * you consent, and collapsing those would mean a mis-tap on a logo starts a
   * payment.
   */
  const selectUpiApp = useCallback(
    (shortcode: string) => {
      if (!directUpiAppSelectionEnabled) {
        logger.warn("[payments] blocked_unsafe_direct_upi_app_selection", {
          platform: upiSurface.platform,
          shortcode,
        });
        setSheetError(null);
        setPaymentMode("upi_intent");
        setSelectedUpiApp(null);
        setSelectedStoredMethod(null);
        setHasChosenPaymentMethod(true);
        setSheetOpen(false);
        emitPaymentEvent("payment_method_selected", {
          selected_payment_mode: "upi_intent",
          selected_saved_payment_method: false,
          selected_payment_method_category: "upi",
          payment_method_category: "upi",
          payment_method_source: "intent",
          upi_flow: "intent",
          upi_handoff_strategy: "provider_checkout",
          requested_upi_app: shortcode,
          unsafe_direct_selection_blocked: true,
        });
        return;
      }

      setSheetError(null);
      setPaymentMode("upi_intent");
      setSelectedUpiApp(shortcode);
      setSelectedStoredMethod(null);
      setHasChosenPaymentMethod(true);
      setSheetOpen(false);
      emitPaymentEvent("payment_method_selected", {
        selected_payment_mode: "upi_intent",
        selected_saved_payment_method: false,
        selected_payment_method_category: "upi",
        payment_method_category: "upi",
        payment_method_source: "intent",
        upi_flow: shortcode === UPI_ANY_APP ? "intent_generic" : "intent",
        selected_upi_app: shortcode,
        upi_handoff_strategy: directUpiHandoffStrategy,
      });
    },
    [
      directUpiAppSelectionEnabled,
      directUpiHandoffStrategy,
      emitPaymentEvent,
      upiSurface.platform,
    ],
  );

  /**
   * Cancel from the QR drawer. Closes the code at the provider so an abandoned
   * booking cannot be paid minutes later from a screenshot — best-effort, since
   * `close_by` already bounds it and a failed close must not block the
   * dismissal.
   */
  const cancelUpiSession = useCallback(() => {
    const active = upiSession;
    if (active?.qrCodeId) {
      void closeUpiQrForPaymentAttempt({ qrCodeId: active.qrCodeId });
      emitPaymentEvent("payment_attempt_failed", {
        payment_method_category: "upi",
        payment_method_source: "qr",
        upi_flow: "qr",
        failure_stage: "upi_qr_dismissed",
      });
    }
    settleUpiSession({ kind: "dismissed" });
  }, [emitPaymentEvent, settleUpiSession, upiSession]);

  /**
   * Watch for the webhook landing while a code is on screen.
   *
   * The poll is the caller's own status check, so it sees exactly what the
   * payment page's pending path sees — no second source of truth about whether
   * a booking is paid. Three seconds is tighter than the five the pending poll
   * uses because somebody is watching this screen waiting for it to change.
   */
  useEffect(() => {
    if (!upiSession || !checkPaymentSettled) return;
    let cancelled = false;

    const timer = window.setInterval(() => {
      void (async () => {
        let settled = false;
        try {
          settled = await checkPaymentSettled();
        } catch {
          // A failed check is not a failed payment. Keep waiting: the code is
          // still live and the next tick may well answer.
          return;
        }
        if (settled && !cancelled) settleUpiSession({ kind: "settled" });
      })();
    }, 3000);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [checkPaymentSettled, settleUpiSession, upiSession]);

  /**
   * Editing the address invalidates the confirmation that went with it. Without
   * this, changing one character after a successful check leaves the previous
   * account holder's name showing under a different VPA.
   */
  const setUpiIdInput = useCallback((value: string) => {
    setUpiId(value);
    setUpiHolderName(null);
    setUpiVerified(false);
  }, []);

  const startAddCard = useCallback(() => {
    setSelectedUpiApp(null);
    setPaymentMode("card");
    setSelectedStoredMethod(null);
    setHasChosenPaymentMethod(false);
    setSheetError(null);
    setSheetView("add_card");
    emitPaymentEvent("payment_method_add_started", {
      selected_payment_mode: "card",
      selected_saved_payment_method: false,
      selected_payment_method_category: "card",
      payment_method_category: "card",
      payment_method_source: "draft",
    });
  }, [emitPaymentEvent]);

  const startAddUpi = useCallback(() => {
    setSelectedUpiApp(null);
    setPaymentMode("upi");
    setSelectedStoredMethod(null);
    setHasChosenPaymentMethod(false);
    setSheetError(null);
    setSheetView("add_upi");
    emitPaymentEvent("payment_method_add_started", {
      selected_payment_mode: "upi",
      selected_saved_payment_method: false,
      selected_payment_method_category: "upi",
      payment_method_category: "upi",
      payment_method_source: "draft",
    });
  }, [emitPaymentEvent]);

  const applyCardDraft = useCallback(() => {
    const error = validateCardDraft();
    if (error) {
      setSheetError(error.message);
      setCardErrorField(error.field);
      emitPaymentEvent("payment_method_validation_failed", {
        selected_payment_mode: "card",
        selected_saved_payment_method: false,
        selected_payment_method_category: "card",
        payment_method_category: "card",
        payment_method_source: "draft",
        reason: error.reason,
        failure_stage: "client_validation",
        invalid_field: error.field,
        payment_outcome: "not_submitted",
        payment_feedback_code: error.reason,
        recovery_action: "edit_card",
      });
      return;
    }
    setSheetError(null);
    setCardErrorField(null);
    setPaymentMode("card");
    setSelectedStoredMethod(null);
    setHasChosenPaymentMethod(true);
    setSheetOpen(false);
    emitPaymentEvent("payment_method_selected", {
      selected_payment_mode: "card",
      selected_saved_payment_method: false,
      selected_payment_method_category: "card",
      payment_method_category: "card",
      payment_method_source: "draft",
      save_payment_method_requested: saveCard,
    });
  }, [emitPaymentEvent, saveCard, validateCardDraft]);

  /**
   * Turn a typed UPI ID into a real payment method.
   *
   * The provider is asked whether the handle exists, and only then is it stored —
   * as `pending`: selectable, removable, and yours to keep several of. It becomes
   * `saved` the first time it actually carries a payment, and from that point the
   * only change anyone can make is removing it.
   *
   * A validator we cannot reach is not a refusal; the server accepts the id and
   * leaves the verdict to the charge. See `validateRazorpayVpa`.
   */
  const applyUpiDraft = useCallback(async () => {
    const error = validateUpiDraft();
    if (error) {
      setSheetError(error);
      emitPaymentEvent("payment_method_validation_failed", {
        selected_payment_mode: "upi",
        selected_saved_payment_method: false,
        selected_payment_method_category: "upi",
        payment_method_category: "upi",
        payment_method_source: "draft",
        reason: "missing_upi_id",
      });
      return;
    }

    // The button is the check. Nothing has asked the provider about this
    // address before now.
    const verified = await checkUpiDraft();
    if (!verified) return;

    const candidate = normalizeVpa(upiId);
    setSheetError(null);
    setIsValidatingUpi(true);
    try {
      const result = await addUpiPaymentMethod({ vpa: candidate });
      if (!result.ok) {
        setSheetError(result.message);
        emitPaymentEvent("payment_method_validation_failed", {
          selected_payment_mode: "upi",
          selected_saved_payment_method: false,
          selected_payment_method_category: "upi",
          payment_method_category: "upi",
          payment_method_source: "draft",
          reason: "vpa_rejected",
        });
        return;
      }

      const saved = result.data;
      setLocallyAddedMethods((current) => [
        saved,
        ...current.filter((method) => method.id !== saved.id),
      ]);
      // An id removed earlier in this session and then re-added must come back.
      setLocallyRemovedMethodIds((current) =>
        current.filter((id) => id !== saved.id),
      );
      setUpiHolderName(saved.metadata?.upiHolderName ?? null);
      setPaymentMode("upi");
      setSelectedStoredMethod(saved);
      setHasChosenPaymentMethod(true);
      setUpiId(saved.metadata?.upiVpa ?? candidate);
      setSheetOpen(false);
      emitPaymentEvent("payment_method_added", {
        payment_method_category: "upi",
        payment_method_source: "draft",
        payment_method_id: saved.id,
        payment_method_status: saved.status,
      });
      emitPaymentEvent("payment_method_selected", {
        selected_payment_mode: "upi",
        selected_saved_payment_method: true,
        selected_payment_method_category: "upi",
        payment_method_category: "upi",
        payment_method_source: "draft",
        payment_method_id: saved.id,
        payment_method_status: saved.status,
      });
    } catch {
      setSheetError(
        "Couldn't save that UPI ID. Check your connection and try again.",
      );
    } finally {
      setIsValidatingUpi(false);
    }
  }, [checkUpiDraft, emitPaymentEvent, upiId, validateUpiDraft]);

  /**
   * Remove a saved method.
   *
   * Optimism would be wrong here: for a card this deletes a credential at
   * Razorpay, and a row that vanished from the sheet while the token survived
   * would be adopted straight back by the next reconciliation — the person would
   * watch their "deleted" card reappear.
   */
  const removeMethod = useCallback(
    async (methodId: number) => {
      setSheetError(null);
      setRemovingMethodId(methodId);
      try {
        const result = await removePaymentMethod({ methodId });
        if (!result.ok) {
          setSheetError(result.message);
          return;
        }
        setLocallyRemovedMethodIds((current) =>
          current.includes(methodId) ? current : [...current, methodId],
        );
        setLocallyAddedMethods((current) =>
          current.filter((method) => method.id !== methodId),
        );
        const removedMethod = methods.find((method) => method.id === methodId);
        emitPaymentEvent("payment_method_removed", {
          payment_method_id: methodId,
          payment_method_category: removedMethod?.category ?? null,
        });
        if (selectedStoredMethod?.id === methodId) {
          setSelectedStoredMethod(null);
          setHasChosenPaymentMethod(false);
          setUpiId("");
          setUpiHolderName(null);
          setUpiVerified(false);
          setCardName("");
          setCardNickname("");
          setCardNumber("");
          setCardExpiry("");
          setCardCvv("");
          clearPaymentMethodDraft();
        }
      } catch {
        setSheetError(
          "Couldn't remove that method. Check your connection and try again.",
        );
      } finally {
        setRemovingMethodId(null);
      }
    },
    [emitPaymentEvent, methods, selectedStoredMethod],
  );

  const getLaunchIdempotencyKey = useCallback(() => {
    if (!reuseIdempotencyKey) return createIdempotencyKey();
    if (!idempotencyKeyRef.current) {
      idempotencyKeyRef.current = createIdempotencyKey();
    }
    return idempotencyKeyRef.current;
  }, [reuseIdempotencyKey]);

  // Drop the pinned key so the next launch starts a fresh payment attempt.
  // Callers must invoke this whenever the amount to charge changes (e.g. a
  // promo or Hiive Money toggle), otherwise a reused key pins the pre-change
  // total and the server rejects the retry as a stale attempt.
  const resetIdempotencyKey = useCallback(() => {
    idempotencyKeyRef.current = null;
  }, []);

  const getPaymentNotes = useCallback(
    (prepared: TPrepared, source: "saved" | "draft"): Record<string, string> =>
      buildNotes({
        prepared,
        mode: paymentMode,
        source,
        selectedMethod: activeSelectedStoredMethod,
        cardNickname,
        saveCard,
        saveUpi,
      }),
    [
      buildNotes,
      cardNickname,
      paymentMode,
      saveCard,
      saveUpi,
      activeSelectedStoredMethod,
    ],
  );

  const confirmFromPayment = useCallback(
    async (
      prepared: TPrepared,
      payment: {
        razorpay_order_id: string;
        razorpay_payment_id: string;
        razorpay_signature?: string;
      },
      savePaymentMethod: boolean,
    ): Promise<ActionResult<TConfirmed>> => {
      // Razorpay has returned a payment id. Clear all sensitive card fields
      // immediately; confirmation can be retried server-side without them.
      setCardNumber("");
      setCardExpiry("");
      setCardCvv("");

      let result: ActionResult<TConfirmed>;
      try {
        result = await confirmPayment({
          referenceId: prepared.referenceId,
          paymentAttemptId: prepared.paymentAttemptId,
          razorpayOrderId: payment.razorpay_order_id,
          razorpayPaymentId: payment.razorpay_payment_id,
          razorpaySignature: payment.razorpay_signature,
          cardNickname:
            paymentMode === "card"
              ? cardNickname.trim() || undefined
              : undefined,
          savePaymentMethod,
        });
      } catch {
        result = {
          ok: false,
          message: PAYMENT_CONFIRMATION_PENDING_MESSAGE,
        };
      }

      const heldAsIntended =
        authorizeOnly &&
        result.ok &&
        typeof result.data === "object" &&
        result.data !== null &&
        "paymentStatus" in result.data &&
        result.data.paymentStatus === "authorized";
      const explicitlyUnconfirmed =
        !heldAsIntended &&
        result.ok &&
        typeof result.data === "object" &&
        result.data !== null &&
        "confirmed" in result.data &&
        (result.data as { confirmed?: unknown }).confirmed === false;

      if (!result.ok || explicitlyUnconfirmed) {
        const refunded =
          result.ok &&
          typeof result.data === "object" &&
          result.data !== null &&
          "paymentStatus" in result.data &&
          result.data.paymentStatus === "refunded";
        emitPaymentEvent(
          refunded ? "payment_attempt_failed" : "payment_outcome_pending",
          {
            payment_attempt_id: prepared.paymentAttemptId,
            payment_outcome: refunded ? "refunded" : "unknown",
            failure_stage: "payment_confirmation",
            reason: result.ok
              ? refunded
                ? "payment_refunded"
                : "confirmation_pending"
              : result.message.slice(0, 120),
            provider_payment_id: payment.razorpay_payment_id,
          },
        );
        return {
          ok: false,
          message: refunded
            ? PAYMENT_REFUNDED_MESSAGE
            : PAYMENT_CONFIRMATION_PENDING_MESSAGE,
        };
      }
      // The money moved, so the draft has done its job. Anything worth keeping
      // is server-side now (that is what `savePaymentMethod` asked for), and
      // holding a payment identifier past the checkout that needed it buys
      // nothing. A failed attempt keeps the draft — that is when it matters.
      if (
        savePaymentMethod &&
        paymentMode === "card" &&
        !activeSelectedStoredMethod
      ) {
        emitPaymentEvent("payment_method_added", {
          payment_method_category: "card",
          payment_method_source: "draft",
        });
      }
      clearPaymentMethodDraft();
      return result;
    },
    [
      authorizeOnly,
      activeSelectedStoredMethod,
      cardNickname,
      confirmPayment,
      emitPaymentEvent,
      paymentMode,
    ],
  );

  const performLaunchPayment = useCallback(async (): Promise<
    ActionResult<TConfirmed>
  > => {
    const keyId = razorpayKeyId;
    if (disabled || !keyId) {
      emitPaymentEvent("payment_validation_failed", {
        reason: "blocked",
      });
      return {
        ok: false,
        message: blockedMessage ?? "This booking cannot be paid right now.",
      };
    }

    if (!hasChosenPaymentMethod) {
      emitPaymentEvent("payment_validation_failed", {
        reason: "missing_payment_method",
      });
      setSheetError(null);
      setSheetView("methods");
      setSheetOpen(true);
      return {
        ok: false,
        message: "Choose a payment method to continue.",
        inputRequired: true,
      };
    }

    if (paymentMode === "upi" && !upiId.trim()) {
      emitPaymentEvent("payment_validation_failed", {
        payment_method_category: "upi",
        reason: "missing_upi_id",
      });
      // Empty field, never touched — the placeholder says what to type, so no
      // error banner on top of it.
      requestPaymentDetails("upi", null);
      return {
        ok: false,
        message: "Enter a UPI ID to continue.",
        inputRequired: true,
      };
    }

    /*
      Last gate before an order exists, and only for a typed draft.

      A draft can reach Pay without ever passing the button — open "Add UPI
      ID", type, dismiss the sheet, press Pay — and that is the path that
      creates an order Razorpay then refuses with no payment under it. A stored
      address already cleared the check when it was added, so re-asking would
      put a ~1.6s round trip in front of the commonest good path to catch an
      address that went stale since; the charge handles that one, and routes it
      back to this sheet with a reason.
    */
    if (
      paymentMode === "upi" &&
      !activeSelectedStoredMethod &&
      !(await checkUpiDraft())
    ) {
      requestPaymentDetails("upi", null);
      setSheetView("add_upi");
      return {
        ok: false,
        message: UPI_ID_NOT_FOUND_MESSAGE,
        inputRequired: true,
      };
    }

    // A courtesy shortcut, not the gate: it saves a round trip when we already
    // know there is no verified number. The authority is the server, which
    // refuses below with PHONE_VERIFICATION_REQUIRED whatever the client thinks.
    if (requiresVerifiedPhone && !verifiedContactPhone) {
      emitPaymentEvent("payment_validation_failed", {
        reason: "missing_verified_phone",
      });
      setSheetView("verify_phone");
      setSheetOpen(true);
      return {
        ok: false,
        message: "Verify your mobile number to continue.",
        code: PHONE_VERIFICATION_REQUIRED,
        inputRequired: true,
      };
    }

    const expiry = parseCardExpiry(cardExpiry);
    if (
      paymentMode === "card" &&
      activeSelectedStoredMethod?.category !== "card"
    ) {
      const cardIssue = getCardDraftValidationError({
        name: cardName,
        number: cardNumber,
        expiry: cardExpiry,
        cvv: cardCvv,
      });
      if (cardIssue) {
        emitPaymentEvent("payment_validation_failed", {
          payment_method_category: "card",
          reason: cardIssue.reason,
          failure_stage: "client_validation",
          invalid_field: cardIssue.field,
          payment_outcome: "not_submitted",
          payment_feedback_code: cardIssue.reason,
          recovery_action: "edit_card",
        });
        setCardErrorField(cardIssue.field);
        // Four fields, so name the one at fault inside the form rather than
        // leaving them to guess which input the message meant.
        requestPaymentDetails("card", cardIssue.message);
        return {
          ok: false,
          message: cardIssue.message,
          inputRequired: true,
        };
      }
    }

    setIsLaunching(true);
    /**
     * Ask the native shell to stop ejecting off-origin navigation, and only
     * commit to redirect mode if it agreed.
     *
     * The two have to move together. Redirect mode is what stops Razorpay
     * reaching for a popup — which iOS ejects to Safari unconditionally — but a
     * redirect on a shell that is still ejecting would land the person on
     * Razorpay's 404 in an external browser, which is the bug being fixed. An
     * older build without the plugin therefore keeps the popup flow: still
     * broken there, but no worse, and it self-heals on the next store release.
     */
    paymentAuditRef.current = null;
    let nativeNavigationOpen = false;
    let attemptWasPrepared = false;
    try {
      try {
        nativeNavigationOpen = await beginNativePaymentNavigation();
      } catch (error) {
        logger.warn("Could not open native payment navigation lease", {
          error: error instanceof Error ? error.message : String(error),
        });
      }

      const [attemptResult, customerLookup] = await Promise.all([
        preparePaymentAttempt({
          referenceId,
          idempotencyKey: getLaunchIdempotencyKey(),
        }).catch(
          (): ActionResult<TPrepared> => ({
            ok: false,
            code: "PAYMENT_PREPARATION_UNAVAILABLE",
            message:
              "Couldn't start payment. Check your connection and try again.",
          }),
        ),
        getOrCreateRazorpayCustomerForUser().catch(() => ({
          ok: false as const,
          message: "Saved payment methods could not be loaded.",
        })),
      ]);

      if (!attemptResult.ok) {
        // The server refused because there is no verified number. Turn that
        // into the verification step rather than a toast: the basket, the
        // selected method and the pinned idempotency key all survive, so
        // pressing Pay again after verifying resumes the same attempt.
        if (
          "code" in attemptResult &&
          attemptResult.code === PHONE_VERIFICATION_REQUIRED
        ) {
          emitPaymentEvent("payment_attempt_failed", {
            failure_stage: "phone_verification_required",
            // The failure panel groups on `reason`; without one this arrives as
            // an unexplained "Could not create the order".
            reason: "missing_verified_phone",
          });
          setVerifiedContactPhone(null);
          setSheetView("verify_phone");
          setSheetOpen(true);
          return { ...attemptResult, inputRequired: true };
        }
        emitPaymentEvent("payment_attempt_failed", {
          failure_stage: "payment_attempt_create",
          reason: attemptResult.code ?? "order_create_failed",
          payment_outcome: "not_submitted",
        });
        return { ok: false, message: attemptResult.message };
      }

      const prepared = attemptResult.data;
      paymentAuditRef.current = {
        payment_attempt_id: prepared.paymentAttemptId,
        provider_order_id: prepared.orderId,
        started_at: Date.now(),
      };
      attemptWasPrepared = true;
      // The server's snapshot is authoritative for everything sent onward to
      // Razorpay. Adopting it here also self-heals a stale prop.
      const preparedPayer = readPreparedPayer(prepared);
      if (preparedPayer) {
        setVerifiedContactPhone(preparedPayer.contactPhone);
        setVerifiedContactEmail(preparedPayer.contactEmail);
      }
      const razorpayContact =
        preparedPayer?.contactPhone ?? verifiedContactPhone ?? undefined;
      const razorpayEmail =
        preparedPayer?.contactEmail ?? verifiedContactEmail ?? undefined;
      // Record only presence, never the contact values themselves. This makes
      // provider failures diagnosable for phone-only payers without putting an
      // email address or phone number into analytics.
      const payerTelemetry = {
        has_payer_contact: Boolean(razorpayContact),
        has_payer_email: Boolean(razorpayEmail),
      };
      emitPaymentEvent("payment_attempt_created", {
        payment_attempt_id: prepared.paymentAttemptId,
        amount_minor: prepared.amountMinor,
        currency: prepared.currency,
        has_razorpay_customer: customerLookup.ok,
        ...payerTelemetry,
      });

      /**
       * Carry the "remember this method" intent into the return URL.
       *
       * In redirect mode `confirmFromPayment` never runs — the document is gone
       * before Razorpay answers — so the return route becomes the only thing that
       * reconciles the payment, and it is where the save has to happen instead.
       * Without this, switching native checkout to redirect would have quietly
       * stopped saving every card and promoting every UPI id, with nothing
       * failing to show for it.
       *
       * Safe as a query param: the route re-reads the booking to find whose
       * method it is, and will not act at all without a valid Razorpay signature.
       * The flag can only ever say "persist the method that just paid for this
       * booking" — it cannot name a different user or a different method.
       */
      const returnUrlFor = (savePaymentMethod: boolean) => {
        const base = buildReturnUrl?.(prepared);
        if (!base || !savePaymentMethod) return base;
        try {
          const url = new URL(base);
          url.searchParams.set("savePaymentMethod", "1");
          return url.toString();
        } catch {
          return base;
        }
      };

      /**
       * The app the payer chose in the sheet, handed off directly.
       *
       * No intermediate screen. The choice already happened in the
       * payment-method sheet, so Pay does exactly what the button says: opens
       * that app. Razorpay's own Intent screen is what this replaces — its
       * tiles are drawn from the user agent (`getSupportedUpiIntentApps()`
       * resolves a static array and never inspects the device), so tapping an
       * app you do not have was routine there, and terminal.
       *
       * Here it is recoverable: the failure reopens the sheet with the reason
       * stated and that app dropped from the list.
       */
      if (
        paymentMode === "upi_intent" &&
        selectedUpiApp &&
        directUpiAppSelectionEnabled
      ) {
        const app =
          upiApps?.apps.find((entry) => entry.shortcode === selectedUpiApp) ??
          null;
        const appName =
          selectedUpiApp === UPI_ANY_APP
            ? "your UPI app"
            : (app?.name ?? "your UPI app");

        /**
         * Built once and reused by every Open App tap.
         *
         * The order, the customer and the SDK are all already resolved by the
         * time the screen is up, so a retry is one synchronous `createPayment`
         * away — which is the whole point. Razorpay permits several payment
         * attempts against one order and only one can succeed; a refused UPI
         * submit creates no payment object at all, so a retry leaves nothing
         * behind to reconcile.
         */
        const paymentData = {
          key: prepared.razorpayKeyId,
          amount: prepared.amountMinor,
          currency: prepared.currency,
          order_id: prepared.orderId,
          method: "upi" as const,
          // Custom Checkout rejects a blank email outright, and a phone-only
          // payer has none. See lib/payments/razorpay-contact.
          email: razorpayPaymentEmail(razorpayEmail),
          contact: razorpayContact,
          ...(customerLookup.ok
            ? { customer_id: customerLookup.data.providerCustomerId }
            : {}),
          notes: getPaymentNotes(prepared, "draft"),
          /**
           * Inside the native shell the SDK lists no app at all unless the
           * paying instance carries `webview_intent`, and then refuses this
           * shortcode locally as `intent_no_apps_error`. The deep link it then
           * fires is a top-level navigation the shell hands to the OS: iOS via
           * Capacitor's `UIApplication.open`, Android via `ACTION_VIEW` (or
           * HiivePaymentNavigation's sanitised launch for `intent://`).
           *
           * No `redirect`/`callback_url` here, as on the web: an app handoff
           * reports back through `payment.success` or the settlement poll, and
           * the card path's redirect lease is untouched.
           */
          ...(nativePaymentShell ? { webview_intent: true } : {}),
        };

        upiIntentSwitchRef.current = { observed: false, at: null };

        /**
         * One deep-link attempt, whose failure is advisory rather than final.
         *
         * `intent_no_apps_error` reaches us on the payer's next touch of a page
         * that never went hidden — see `UpiIntentHandoff`. It cannot tell a
         * missing app from a declined "Open in PhonePe?" prompt from a scheme
         * Safari would have honoured inside a gesture, so it marks the screen
         * and leaves Open App on it. Only a real provider refusal closes this.
         */
        const runAttempt = (attemptNumber: number) => {
          void createRazorpayCustomPayment(paymentData, {
            app: selectedUpiApp,
          }).then((payment) => {
            if (upiIntentResolverRef.current === null) return;

            if (payment.ok) {
              settleUpiIntentHandoff({ kind: "paid", payment: payment.data });
              return;
            }

            const noApp = isUpiNoAppFailure({
              reason: payment.providerError?.reason,
              message: payment.message,
            });
            const switchObserved = upiIntentSwitchRef.current.observed;

            emitPaymentEvent("payment_attempt_failed", {
              payment_method_category: "upi",
              payment_method_source: "intent",
              upi_flow:
                selectedUpiApp === UPI_ANY_APP ? "intent_generic" : "intent",
              selected_upi_app: selectedUpiApp,
              failure_stage: noApp ? "upi_no_app" : "razorpay_custom_payment",
              upi_handoff_strategy: directUpiHandoffStrategy,
              handoff_attempts: attemptNumber,
              app_hidden_after_failure: false,
              /**
               * The one thing that separates the causes the provider reports
               * identically. A no-app verdict on a document that was never
               * hidden is a link that opened nothing; one after a switch is a
               * payer who reached their app and came back.
               */
              app_switch_observed: switchObserved,
              ...(noApp
                ? {
                    handoff_failure_classification:
                      "app_unavailable_or_prompt_cancelled",
                  }
                : {}),
              ...providerErrorProperties(payment),
              ...payerTelemetry,
            });

            if (noApp) {
              logger.warn("[payments] upi_app_handoff_not_completed", {
                platform: upiSurface.platform,
                shortcode: selectedUpiApp,
                providerReason: payment.providerError?.reason ?? "unknown",
                appHidden: false,
                appSwitchObserved: switchObserved,
                attempts: attemptNumber,
                classification: "app_unavailable_or_prompt_cancelled",
              });
              setUpiIntentHandoff((current) =>
                current
                  ? { ...current, retrying: false, appOpenFailed: true }
                  : current,
              );
              return;
            }

            // A genuine refusal — a decline, a dead order. Nothing an Open App
            // tap can change, so it ends the screen with the provider's reason.
            settleUpiIntentHandoff({
              kind: "failed",
              message: payment.message,
            });
          });
        };

        emitPaymentEvent("payment_attempt_created", {
          payment_attempt_id: prepared.paymentAttemptId,
          payment_method_category: "upi",
          payment_method_source: "intent",
          upi_flow:
            selectedUpiApp === UPI_ANY_APP ? "intent_generic" : "intent",
          selected_upi_app: selectedUpiApp,
          upi_handoff_strategy: directUpiHandoffStrategy,
          ...payerTelemetry,
        });

        const outcome = await new Promise<UpiIntentOutcome>((resolve) => {
          upiIntentResolverRef.current = resolve;
          upiIntentAttemptRef.current = () => {
            upiIntentHandoffAttemptsRef.current += 1;
            runAttempt(upiIntentHandoffAttemptsRef.current);
          };
          upiIntentHandoffAttemptsRef.current = 1;
          setUpiIntentHandoff({
            shortcode: selectedUpiApp,
            appName,
            iconUrl: app?.iconUrl ?? null,
            amountMinor: prepared.amountMinor,
            currency: prepared.currency,
            merchantName,
            attempts: 1,
            retrying: false,
            appOpenFailed: false,
          });
          runAttempt(1);
        });

        if (outcome.kind === "paid") {
          return confirmFromPayment(prepared, outcome.payment, false);
        }

        if (outcome.kind === "failed") {
          return { ok: false, message: outcome.message };
        }

        /**
         * Dismissed, and what that meant depends on whether an app was ever
         * reached.
         *
         * No app switch means nothing was initiated: send the payer back to the
         * method list with the reason stated, and `inputRequired` so the page
         * does not cover it with a "Payment Failed" panel they cannot act on.
         * A switch means money may be in flight, which must never be reported
         * as a failure — that goes down the same unknown-outcome path an
         * abandoned collect already takes.
         */
        if (outcome.kind === "settled" || upiIntentSwitchRef.current.observed) {
          return { ok: false, message: UPI_QR_AWAITING_MESSAGE };
        }

        emitPaymentEvent("payment_attempt_failed", {
          payment_method_category: "upi",
          payment_method_source: "intent",
          upi_flow:
            selectedUpiApp === UPI_ANY_APP ? "intent_generic" : "intent",
          selected_upi_app: selectedUpiApp,
          failure_stage: "upi_handoff_dismissed",
          upi_handoff_strategy: directUpiHandoffStrategy,
          app_switch_observed: false,
          handoff_attempts: upiIntentHandoffAttemptsRef.current,
          app_hidden_after_failure: false,
        });
        setSheetError(UPI_NO_APP_MESSAGE);
        setSheetView("methods");
        setSheetOpen(true);
        return {
          ok: false,
          message: UPI_NO_APP_MESSAGE,
          inputRequired: true,
        };
      }

      if (paymentMode === "upi_qr") {
        const qr = supportsUpiQr
          ? await createUpiQrForPaymentAttempt({
              amountMinor: prepared.amountMinor,
              description,
              notes: getPaymentNotes(prepared, "draft"),
              customerId: customerLookup.ok
                ? customerLookup.data.providerCustomerId
                : null,
            })
          : null;

        if (!qr?.ok) {
          emitPaymentEvent("payment_attempt_failed", {
            payment_method_category: "upi",
            payment_method_source: "qr",
            upi_flow: "qr",
            failure_stage: "upi_qr_create",
            reason: qr?.message?.slice(0, 120) ?? "qr_unavailable",
            ...payerTelemetry,
          });
          setSelectedStoredMethod(null);
          setHasChosenPaymentMethod(false);
          setSheetError(
            "QR payments aren't available right now. Pick another way to pay.",
          );
          setSheetView("methods");
          setSheetOpen(true);
          return {
            ok: false,
            message:
              "QR payments aren't available right now. Please use another payment method.",
            inputRequired: true,
          };
        }

        emitPaymentEvent("payment_attempt_created", {
          payment_attempt_id: prepared.paymentAttemptId,
          payment_method_category: "upi",
          payment_method_source: "qr",
          upi_flow: "qr",
          ...payerTelemetry,
        });

        await new Promise<UpiDrawerOutcome>((resolve) => {
          upiResolverRef.current = resolve;
          setUpiSession({
            qrCodeId: qr.data.qrCodeId,
            upiUri: qr.data.upiUri,
            expiresAtIso: qr.data.expiresAtIso,
            amountMinor: qr.data.amountMinor,
            currency: prepared.currency,
            merchantName,
          });
        });

        /**
         * Neither outcome is a result. Settled means the caller's status check
         * already navigated; dismissed means money may still be in flight. Both
         * hand off through `isPaymentOutcomeUnknown` — the path an abandoned
         * collect already takes — so a live payment never gets a "Payment
         * Failed" screen over the top of it.
         */
        return { ok: false, message: UPI_QR_AWAITING_MESSAGE };
      }

      if (paymentMode === "upi_intent") {
        const stopHandoffWatch = watchUpiHandoff({
          payment_method_category: "upi",
          payment_method_source: "intent",
          upi_handoff_strategy: "provider_checkout",
        });
        let checkout: Awaited<ReturnType<typeof openRazorpayCheckout>>;
        let handoff: UpiHandoffObservation;
        try {
          checkout = await openRazorpayCheckout(
            buildRazorpayUpiIntentCheckoutOptions({
              key: prepared.razorpayKeyId,
              amountMinor: prepared.amountMinor,
              currency: prepared.currency,
              merchantName,
              description,
              orderId: prepared.orderId,
              callbackUrl: returnUrlFor(false),
              nativeWebView: isNativePaymentShell(),
              redirectToCallback: nativeNavigationOpen,
              payer: {
                name: payer.name ?? undefined,
                email: razorpayEmail,
                contact: razorpayContact,
              },
              customerId: customerLookup.ok
                ? customerLookup.data.providerCustomerId
                : undefined,
              notes: getPaymentNotes(prepared, "draft"),
              themeColor,
            }),
          );
        } finally {
          handoff = stopHandoffWatch();
        }

        if (!checkout.ok) {
          const noApp = isUpiNoAppFailure({
            reason: checkout.providerError?.reason,
            message: checkout.message,
          });

          emitPaymentEvent("payment_attempt_failed", {
            payment_method_category: "upi",
            payment_method_source: "intent",
            upi_flow: "intent",
            failure_stage: noApp ? "upi_no_app" : "razorpay_checkout",
            upi_handoff_strategy: "provider_checkout",
            app_hidden_after_failure: false,
            /**
             * Did any app ever come to the foreground before this failed?
             *
             * The one thing that separates the two causes the provider reports
             * identically: a payer who reached their app and backed out went
             * through a document that was hidden, and an `intent_no_apps_error`
             * on a document that was never hidden is a deep link that never
             * opened anything.
             */
            app_switch_observed: handoff.appSwitchObserved,
            ...(handoff.msToAppSwitch === null
              ? {}
              : { ms_to_app_switch: handoff.msToAppSwitch }),
            ...(noApp
              ? {
                  handoff_failure_classification:
                    "app_unavailable_or_prompt_cancelled",
                }
              : {}),
            /**
             * The whole point of reading `payment.failed`. Before this, every
             * mobile-web intent failure — no app installed, payer changed their
             * mind, bank declined — arrived as one unlabelled event, so the most
             * common cause of a dead checkout was invisible in the funnel.
             */
            reason:
              checkout.providerError?.reason ??
              checkout.message?.slice(0, 120) ??
              "unknown",
            provider_error_code: checkout.providerError?.code ?? undefined,
            ...payerTelemetry,
          });

          /**
           * A missing app is recoverable, so recover instead of reporting.
           *
           * Nothing was charged and nothing is in flight — the payer picked an
           * app they do not have off a list that never claimed to know. Sending
           * them back to our own picker (which has the QR, their cards and a UPI
           * ID in it) with the reason stated is the only useful next screen. The
           * `inputRequired` flag is what stops the payment pages covering it
           * with a "Payment Failed" panel they cannot act on.
           */
          if (noApp) {
            logger.warn("[payments] managed_upi_handoff_not_completed", {
              platform: upiSurface.platform,
              providerReason: checkout.providerError?.reason ?? "unknown",
              appHidden: false,
              appSwitchObserved: handoff.appSwitchObserved,
              classification: "app_unavailable_or_prompt_cancelled",
            });
            setSheetError(UPI_NO_APP_MESSAGE);
            setSheetView("methods");
            setSheetOpen(true);
            return {
              ok: false,
              message: UPI_NO_APP_MESSAGE,
              inputRequired: true,
            };
          }

          return checkout;
        }

        return confirmFromPayment(prepared, checkout.data, false);
      }

      if (activeSelectedStoredMethod?.category === "card") {
        if (!customerLookup.ok) {
          emitPaymentEvent("payment_attempt_failed", {
            payment_method_category: "card",
            payment_method_source: "saved",
            failure_stage: "saved_customer_lookup",
          });
          setSelectedStoredMethod(null);
          setHasChosenPaymentMethod(false);
          setSheetError(
            "That saved card couldn't be loaded. Add it again or pick another method.",
          );
          setSheetView("methods");
          setSheetOpen(true);
          return {
            ok: false,
            message: "Saved card could not be loaded. Add the card again.",
            inputRequired: true,
          };
        }

        const token = getReusablePaymentToken(activeSelectedStoredMethod);
        if (token) {
          const payment = await createRazorpayCustomPayment({
            key: prepared.razorpayKeyId,
            amount: prepared.amountMinor,
            currency: prepared.currency,
            order_id: prepared.orderId,
            method: "card",
            callback_url: returnUrlFor(true),
            redirect: nativeNavigationOpen && Boolean(buildReturnUrl),
            token,
            // Custom Checkout rejects a blank email outright, and a phone-only
            // payer has none. See lib/payments/razorpay-contact.
            email: razorpayPaymentEmail(razorpayEmail),
            contact: razorpayContact,
            customer_id: customerLookup.data.providerCustomerId,
            notes: getPaymentNotes(prepared, "saved"),
          });

          if (!payment.ok) {
            const feedback = getCardPaymentFeedback(payment);
            emitPaymentEvent("payment_attempt_failed", {
              ...cardFeedbackProperties(feedback),
              payment_method_category: "card",
              payment_method_source: "saved",
              failure_stage: "razorpay_custom_payment",
              ...providerErrorProperties(payment),
              ...payerTelemetry,
            });
            if (isPaymentOutcomeUnknown(payment.message)) return payment;
            setSelectedStoredMethod(null);
            setHasChosenPaymentMethod(false);
            setSheetError(feedback.message);
            setSheetView("methods");
            setSheetOpen(true);
            return { ...payment, inputRequired: true };
          }

          return confirmFromPayment(prepared, payment.data, true);
        }

        const savedCardReturnUrl = returnUrlFor(true);
        const useSavedCardCallback =
          nativeNavigationOpen && Boolean(savedCardReturnUrl);
        const checkout = await openRazorpayCheckout(
          {
            key: prepared.razorpayKeyId,
            amount: prepared.amountMinor,
            currency: prepared.currency,
            name: merchantName,
            description,
            order_id: prepared.orderId,
            customer_id: customerLookup.data.providerCustomerId,
            // Same bargain as the custom-checkout paths above: on a shell that has
            // opened the payment window, redirect rather than let the modal reach
            // for a popup iOS would eject.
            ...(useSavedCardCallback && savedCardReturnUrl
              ? { callback_url: savedCardReturnUrl }
              : {}),
            redirect: useSavedCardCallback,
            prefill: {
              name: payer.name ?? undefined,
              email: razorpayEmail,
              contact: razorpayContact,
              method: "card",
            },
            retry: { enabled: true },
            config: {
              display: {
                blocks: {
                  cards: {
                    name: "Cards",
                    instruments: [{ method: "card" }],
                  },
                },
                sequence: ["block.cards"],
                preferences: {
                  show_default_blocks: false,
                },
              },
            },
            notes: getPaymentNotes(prepared, "saved"),
            theme: {
              color: themeColor,
            },
          },
          (failure) =>
            emitPaymentEvent("payment_provider_failed", {
              payment_method_category: "card",
              payment_method_source: "saved",
              failure_stage: "razorpay_checkout",
              provider_retry_enabled: true,
              ...providerErrorProperties(failure),
              ...cardFeedbackProperties(getCardPaymentFeedback(failure)),
            }),
        );

        if (!checkout.ok) {
          const feedback = getCardPaymentFeedback(checkout);
          emitPaymentEvent("payment_attempt_failed", {
            ...providerErrorProperties(checkout),
            ...cardFeedbackProperties(feedback),
            payment_method_category: "card",
            payment_method_source: "saved",
            failure_stage: "razorpay_checkout",
            ...payerTelemetry,
          });
          if (isPaymentOutcomeUnknown(checkout.message)) return checkout;
          setSelectedStoredMethod(null);
          setHasChosenPaymentMethod(false);
          setSheetError(feedback.message);
          setSheetView("methods");
          setSheetOpen(true);
          return { ...checkout, inputRequired: true };
        }

        return confirmFromPayment(prepared, checkout.data, true);
      }

      const payment = await createRazorpayCustomPayment({
        key: prepared.razorpayKeyId,
        amount: prepared.amountMinor,
        currency: prepared.currency,
        order_id: prepared.orderId,
        method: paymentMode,
        callback_url: returnUrlFor(paymentMode === "card" ? saveCard : true),
        // Native only, and only with a return route to land on: redirect mode
        // hands confirmation to `/api/razorpay/*-return`, so without one the
        // payment would capture with nothing to reconcile it in-session.
        redirect: nativeNavigationOpen && Boolean(buildReturnUrl),
        email: razorpayPaymentEmail(razorpayEmail),
        contact: razorpayContact,
        customer_id: customerLookup.ok
          ? customerLookup.data.providerCustomerId
          : undefined,
        save: paymentMode === "card" && saveCard ? 1 : undefined,
        notes: getPaymentNotes(
          prepared,
          activeSelectedStoredMethod ? "saved" : "draft",
        ),
        ...(paymentMode === "upi"
          ? { vpa: upiId.trim() }
          : {
              "card[name]": cardName.trim(),
              "card[number]": normalizeDigits(cardNumber),
              "card[cvv]": cardCvv,
              "card[expiry_month]": expiry?.month,
              "card[expiry_year]": expiry?.year,
            }),
      });

      if (!payment.ok) {
        const cardFeedback =
          paymentMode === "card" ? getCardPaymentFeedback(payment) : null;
        emitPaymentEvent("payment_attempt_failed", {
          ...(cardFeedback ? cardFeedbackProperties(cardFeedback) : {}),
          // Was missing entirely on this branch, which is the one UPI takes —
          // so every UPI refusal was indistinguishable from a card refusal.
          payment_method_category: paymentMode,
          payment_method_source: activeSelectedStoredMethod ? "saved" : "draft",
          failure_stage: "razorpay_custom_payment",
          ...providerErrorProperties(payment),
          ...payerTelemetry,
        });

        /*
          A refused address is a request to fix a field, not a failed payment.

          Treated as the latter it was a closed loop: a full-screen "Payment
          Failed" showing Razorpay's own wording, over a "Try again" that
          re-fired the identical stored UPI ID, with the bad address still
          selected underneath. Nothing on the screen said which of the details
          was wrong, and nothing let the person change it.

          So it takes the same route as a missing card field — deselect the
          address the provider just refused, reopen the sheet with it loaded so
          the typo is visible and editable, and hand back `inputRequired` so the
          caller stands down instead of covering the drawer.
        */
        if (paymentMode === "upi" && isVpaRejection(payment.providerError)) {
          setSelectedStoredMethod(null);
          setHasChosenPaymentMethod(false);
          setSheetError(UPI_ID_REJECTED_MESSAGE);
          setSheetView("add_upi");
          setSheetOpen(true);
          // The charge has now given a real verdict on this address, and it
          // outranks whatever the pre-check said. Clear it so the field
          // re-asks rather than trusting a stale pass.
          probedVpaRef.current = null;
          return {
            ok: false,
            message: UPI_ID_REJECTED_MESSAGE,
            inputRequired: true,
          };
        }

        if (
          paymentMode === "card" &&
          !isPaymentOutcomeUnknown(payment.message)
        ) {
          setSheetError(cardFeedback?.message ?? "Try another card or UPI.");
          setCardErrorField(cardFeedback?.field ?? null);
          setSheetView("add_card");
          setSheetOpen(true);
          return { ...payment, inputRequired: true };
        }

        return payment;
      }

      return confirmFromPayment(
        prepared,
        payment.data,
        // A UPI row already exists by the time it can be charged, and the capture
        // is exactly what promotes it from `pending` to `saved`, so it always
        // persists. A card is still the person's choice.
        paymentMode === "card" ? saveCard : true,
      );
    } catch (error) {
      logger.error("Payment launch threw", {
        error: error instanceof Error ? error.message : String(error),
        attemptWasPrepared,
      });
      emitPaymentEvent("payment_attempt_failed", {
        failure_stage: attemptWasPrepared
          ? "payment_or_confirmation"
          : "payment_attempt_create",
        reason: attemptWasPrepared
          ? "payment_status_unknown"
          : "payment_prepare_exception",
        payment_outcome: attemptWasPrepared ? "unknown" : "not_submitted",
      });
      return {
        ok: false,
        message: attemptWasPrepared
          ? PAYMENT_CONFIRMATION_PENDING_MESSAGE
          : "Couldn't start payment. Check your connection and try again.",
      };
    } finally {
      setIsLaunching(false);
      // Only reached when the payment resolved in-page. The redirect path never
      // gets here — the document is gone — which is why the native lease also
      // expires on its own and every checkout screen clears it on mount.
      await endNativePaymentNavigation();
    }
  }, [
    blockedMessage,
    checkUpiDraft,
    referenceId,
    buildReturnUrl,
    cardCvv,
    cardExpiry,
    cardName,
    cardNumber,
    activeSelectedStoredMethod,
    confirmFromPayment,
    description,
    disabled,
    emitPaymentEvent,
    getLaunchIdempotencyKey,
    getPaymentNotes,
    hasChosenPaymentMethod,
    merchantName,
    payer.name,
    paymentMode,
    requiresVerifiedPhone,
    verifiedContactEmail,
    verifiedContactPhone,
    preparePaymentAttempt,
    razorpayKeyId,
    requestPaymentDetails,
    saveCard,
    themeColor,
    upiId,
    supportsUpiQr,
    upiApps?.apps.find,
    directUpiAppSelectionEnabled,
    directUpiHandoffStrategy,
    nativePaymentShell,
    selectedUpiApp,
    settleUpiIntentHandoff,
    upiSurface.platform,
    watchUpiHandoff,
  ]);

  const launchPromiseRef = useRef<Promise<ActionResult<TConfirmed>> | null>(
    null,
  );
  const launchGenerationRef = useRef(0);

  const launchPayment = useCallback((): Promise<ActionResult<TConfirmed>> => {
    if (launchPromiseRef.current) {
      logger.info("Payment launch joined an in-flight launch");
      emitPaymentEvent("payment_launch_coalesced", {
        reason: "launch_already_in_flight",
      });
      return launchPromiseRef.current;
    }

    const generation = launchGenerationRef.current;
    const promise = performLaunchPayment().finally(() => {
      if (
        launchGenerationRef.current === generation &&
        launchPromiseRef.current === promise
      ) {
        launchPromiseRef.current = null;
      }
    });
    launchPromiseRef.current = promise;
    return promise;
  }, [emitPaymentEvent, performLaunchPayment]);

  const resetLaunch = useCallback(() => {
    launchGenerationRef.current += 1;
    launchPromiseRef.current = null;
    setIsLaunching(false);
  }, []);

  const startPhoneVerification = useCallback(() => {
    setSheetError(null);
    setSheetView("verify_phone");
    setSheetOpen(true);
  }, []);

  const completePhoneVerification = useCallback(async () => {
    const refreshed = await refreshPayer?.();
    if (refreshed) {
      setVerifiedContactPhone(refreshed.contact);
      setVerifiedContactEmail(refreshed.email);
    }
    setSheetError(null);
    /*
     * Back to review, NOT straight to a charge. An OTP proved a phone number;
     * it did not authorise a payment.
     *
     * "Review" is the page, not this sheet's picker. Whoever got here by
     * pressing Pay already has a method selected — that is why the gate was the
     * only thing left to fail — so leaving the picker open would hide the Pay
     * button behind a screen with nothing left to choose. Stay open only when
     * there genuinely is no method yet, which is the path from the pages that
     * offer "Verify mobile number" up front.
     */
    setSheetView("methods");
    if (hasChosenPaymentMethod) setSheetOpen(false);
  }, [hasChosenPaymentMethod, refreshPayer]);

  return {
    cardOnly,
    paymentMode,
    hasChosenPaymentMethod,
    selectedStoredMethod: activeSelectedStoredMethod,
    selectedPaymentLabel,
    selectedPaymentSubtitle,
    selectedPaymentBadge,
    selectedPaymentIconUrl,
    storedCards,
    storedUpiMethods,
    sheetOpen,
    sheetView,
    sheetError,
    cardErrorField,
    cardName,
    cardNickname,
    cardNumber,
    cardExpiry,
    cardCvv,
    saveCard,
    saveUpi,
    verifiedContactPhone,
    isVerifyingPhone: sheetView === "verify_phone",
    isValidatingUpi,
    removingMethodId,
    removeMethod,
    upiHolderName,
    upiVerified,
    isLaunching,
    setSheetOpen: (open) => {
      setSheetOpen(open);
      if (!open) {
        setSheetView("methods");
        setSheetError(null);
      }
    },
    setSheetView,
    setCardName,
    setCardNickname,
    setCardNumberInput: (value) => setCardNumber(normalizeCardNumber(value)),
    setCardExpiryInput: (value) => setCardExpiry(normalizeCardExpiry(value)),
    setCardCvvInput: (value) => setCardCvv(normalizeDigits(value).slice(0, 4)),
    upiId,
    setUpiId: setUpiIdInput,
    setSaveCard,
    setSaveUpi,
    openMethodSheet,
    closeMethodSheet,
    selectStoredMethod,
    selectUpiIntent,
    selectUpiQr,
    selectUpiApp,
    upiApps,
    upiAppProbeComplete,
    selectedUpiApp,
    nativePaymentShell,
    providerIntentRecommended,
    upiIntentHandoff,
    retryUpiIntentApp,
    cancelUpiIntentHandoff,
    upiSurface,
    supportsUpiQr,
    upiSession,
    cancelUpiSession,
    startAddCard,
    startAddUpi,
    applyCardDraft,
    applyUpiDraft,
    startPhoneVerification,
    completePhoneVerification,
    launchPayment,
    resetLaunch,
    resetIdempotencyKey,
  };
}
